import * as joint from '@joint/core';
import {
    FlowEditorOptions,
    ShapeType,
    TransactionType,
    FlowType,
    Jurisdiction,
    PaymentType,
    ConnectorStyle,
    EntityNodeOptions,
    MenuOption,
    ToolMode,
    toolModesWithTargetSelection,
    ResizeDirection,
    AlignmentDirection,
    Rect,
    LayoutFormation,
    LayoutItem,
    DragToolMode,
    TargetSelectionToolMode,
    AvailableIconNames,
    IFillColorable,
    ITextColorable,
    isConnectorStyleable,
} from './types';
import {
    CanvasZoom,
    DefaultShapeColors,
    linkTypeColors,
    getFlowTypeInfo,
    getDefaultLinkConnectorStyle,
    setDefaultLinkConnectorStyle,
    toConnectorStyle,
    applyConnectorStyleMixin,
} from './constants';
import { isIconLabel } from './icons';
import { Node } from './node-base';
import {
    CorporationCFC,
    Branch,
    ThirdParty,
    BranchThirdParty,
    Partnership,
    HybridEntity,
    ReverseHybrid,
    Disregarded,
    EntityGroup,
    createEntityNode,
} from './entity-nodes';
import { PhysicalLink, LegalLink, PaymentLink, createFlowLink, createFlowLinkTextLabel } from './flow-links';
import {
    IconElement,
    DrawnRectangle,
    DrawnLine,
    DrawnLineSourceTool,
    DrawnLineTargetTool,
    TextBox,
    Callout,
    GroupSelection,
} from './drawable-elements';
import { applyGraphBackCompatAndCleanup } from './graph-compat';
import { applyOrgChartStylingToNode, resolveDiagramType, measureLabelText, getFlagImageUrl } from './org-chart-styling';
import { MutationDescriptor, EntityCatalogEntry } from './graph-mutations';
import { applyLiveGraphMutation } from './live-graph-mutation';
import { collapseCoincidentFlows, expandMergedFlows, getMergedFlows, ownedStepIds } from './flow-merge';
import { classifyCell, isLiteralFlowEndpoint, toCapabilityId } from './capability-registry';
import {
    addLinkIconLabel as addGraphLinkIconLabel,
    addLinkTextLabel as addGraphLinkTextLabel,
    removeLinkLabel as removeGraphLinkLabel,
    setConnectorStyle as setGraphConnectorStyle,
    setElementProperty as setGraphElementProperty,
    tailTipToLocal,
    updateLinkTextLabel as updateGraphLinkTextLabel,
} from './graph-actions';

type FlowEndpoint = Node | TextBox | IconElement;
type FlowEndpointRole = 'source' | 'target';
type FlowEndpointFeedback = 'valid' | 'invalid';

export function registerGraphGenerationTracking(
    graph: { on(events: string, callback: () => void): unknown },
    incrementGeneration: () => void,
): void {
    // fromJSON/resetCells emits one reset rather than forwarding per-cell add/remove events.
    // Tracking reset is therefore required both for diagram switches and overlapping batches.
    graph.on('change add remove reset', incrementGeneration);
}

type FlowDragState = {
    sourceElement: FlowEndpoint;
    tempLink: joint.shapes.standard.Link | null;
    currentTarget: FlowEndpoint | null;
    highlightedTarget: joint.dia.Element | null;
    mode: DragToolMode;
    dragStartClientPoint?: { x: number; y: number } | null;
};

export class FlowEditor {
    private readonly namespace = joint.shapes;
    private paper: joint.dia.Paper;
    private graph: joint.dia.Graph;
    private dotNetHelper: any;
    private readonly futureStateMode: boolean;
    private readonly showEntityActions: boolean;
    private readonly container: HTMLElement;
    private graphGeneration = 0;
    private operationEpoch = 0;

    // context variables
    private activeTool: ToolMode | null = null;
    // for adding flows
    private flowTypeToCreate: FlowType | null = null;
    // for adding transactions
    private transactionTypeToCreate: TransactionType | null = null;
    // for move-function mode
    private moveFunctionSourceNode: Node | null = null;
    private moveFunctionSourceEntityId: string | null = null;
    private hoveredElementClickDisabled: boolean = false;
    // for drag-to-create flows
    private flowDragState: FlowDragState | null = null;
    private readonly flowDragMoveHandler = (evt: PointerEvent) => this.onFlowDragMove(evt);
    private readonly flowDragEndHandler = (evt: PointerEvent) => this.onFlowDragEnd(evt);
    private readonly flowEndpointHoverHighlighterId = 'flow-endpoint-hover-highlighter';
    private readonly flowDragSourceHighlighterId = 'flow-drag-source-highlighter';
    // for moving functions
    private functionNameToMove: string | null = null;
    // for drawing rectangles
    private startRectangle: { x: number, y: number } | null = null;
    private tempRectangleElement: DrawnRectangle | null = null;
    // for drawing text boxes
    private startTextBox: { x: number, y: number } | null = null;
    private tempTextBoxElement: TextBox | null = null;
    // for drawing callouts
    private startCallout: { x: number, y: number } | null = null;
    private tempCalloutElement: Callout | null = null;
    // for drawing lines
    private startLine: { x: number, y: number } | null = null;
    private tempLineElement: DrawnLine | null = null;
    // for dropping icons
    private iconNameToPlace: string | null = null;
    // for panning the paper
    private panStartPosition: { x: number, y: number } | null = null;
    // for shift-drag multi selection
    private shiftSelectionStart: { x: number; y: number; } | null = null;
    private tempGroupSelectionElement: GroupSelection | null = null;
    private readonly shiftSelectionMoveHandler = (evt: PointerEvent) => this.onShiftSelectionMove(evt);
    private readonly shiftSelectionEndHandler = (evt: PointerEvent) => this.onShiftSelectionEnd(evt);
    // Track generated ids for menus/sub-menus to maintain ancestry chain
    private menuIdCounter = 0;
    // for generic element resizing
    private resizeState: {
        element: joint.dia.Element;
        elementView: joint.dia.ElementView;
        direction: ResizeDirection;
        startPointer: { x: number; y: number; };
        elementStartPosition: { x: number; y: number; };
        elementStartSize: { width: number; height: number; };
        elementOriginalRight: number;
        elementOriginalBottom: number;
        minSize: { width: number; height: number; };
    } | null = null;
    private readonly resizeMoveHandler = (evt: PointerEvent) => this.onElementResizeMove(evt);
    private readonly resizeEndHandler = (evt: PointerEvent) => this.onElementResizeEnd(evt);
    // for dragging callout tails
    private calloutTailDragState: { callout: Callout; } | null = null;
    private readonly calloutTailMoveHandler = (evt: PointerEvent) => this.onCalloutTailMove(evt);
    private readonly calloutTailEndHandler = (evt: PointerEvent) => this.onCalloutTailEnd(evt);
    private readonly escapeKeyHandler = (evt: KeyboardEvent) => this.onEscapeKey(evt);
    private readonly deleteKeyHandler = (evt: KeyboardEvent) => this.onDeleteKey(evt);
    private readonly canvasDragOverHandler = (evt: DragEvent) => this.onCanvasDragOver(evt);
    private readonly canvasDragEnterHandler = (evt: DragEvent) => this.onCanvasDragEnter(evt);
    private readonly canvasDragLeaveHandler = (evt: DragEvent) => this.onCanvasDragLeave(evt);
    private readonly canvasDropHandler = (evt: DragEvent) => this.onCanvasDrop(evt);
    private readonly resizeDefaultMinSize = { width: 80, height: 40 };
    
    // Properties panel selection for non-group-selectable links (all links except DrawnLine).
    private selectedLink: joint.shapes.standard.Link | null = null;
    
    // Multi-selection grouping - cells selected together for group operations (move, align, etc.)
    private groupSelection: GroupSelection | null = null;
    private readonly groupLayoutGap = 20;

    // Entity action bar floating over selected entity
    private entityActionBarEl: HTMLDivElement | null = null;
    private selectedEntityNode: Node | null = null;

    private ensureMenuId(menu: HTMLElement | undefined): string | null {
        if (!menu) return null;
        if (!menu.dataset.menuId) {
            this.menuIdCounter += 1;
            menu.dataset.menuId = String(this.menuIdCounter);
        }
        return menu.dataset.menuId!;
    }

    private collectAncestorIds(startMenu: HTMLElement | undefined | null): Set<string> {
        const ids = new Set<string>();
        let current = startMenu as HTMLElement | null;
        while (current) {
            const id = current.dataset.menuId;
            if (id) ids.add(id);
            const parentId = current.dataset.parentMenuId;
            if (!parentId) break;
            const parentEl = document.querySelector(`[data-menu-id="${parentId}"]`) as HTMLElement | null;
            current = parentEl;
        }
        return ids;
    }
    
    private removeTools() {
        this.paper.removeTools();
        // also hide any Callout tail handles
        this.graph.getElements().forEach(element => {
            if (element instanceof Callout) {
                element.hideTailHandle();
            }
        });
    }

    private setToolCursorActive(active: boolean): void {
        this.container.classList.toggle('flow-tool-active', active);
        this.container.style.cursor = active ? 'crosshair' : '';
    }

    private showFlowEndpointFeedback(
        elementView: joint.dia.ElementView,
        feedback: FlowEndpointFeedback,
        highlighterId: string,
    ): void {
        joint.highlighters.addClass.add(elementView, 'root', highlighterId, {
            className: feedback === 'valid' ? 'flow-endpoint-valid' : 'flow-endpoint-invalid',
        });
    }

    private showHoveredFlowEndpointFeedback(elementView: joint.dia.ElementView, feedback: FlowEndpointFeedback): void {
        joint.highlighters.addClass.removeAll(this.paper, this.flowEndpointHoverHighlighterId);
        this.showFlowEndpointFeedback(elementView, feedback, this.flowEndpointHoverHighlighterId);
    }

    private clearFlowEndpointFeedback(element: joint.dia.Element | null | undefined, highlighterId: string): void {
        if (!element) {
            return;
        }

        const elementView = this.paper.findViewByModel(element) as joint.dia.ElementView | null;
        if (elementView) {
            joint.highlighters.addClass.remove(elementView, highlighterId);
        }
    }

    private clearAllFlowEndpointFeedback(): void {
        joint.highlighters.addClass.removeAll(this.paper, this.flowEndpointHoverHighlighterId);
        joint.highlighters.addClass.removeAll(this.paper, this.flowDragSourceHighlighterId);
        this.hoveredElementClickDisabled = false;
    }

    private isFlowEndpoint(element: joint.dia.Cell | null | undefined): element is FlowEndpoint {
        // Eligible registry kinds are all members of FlowEndpoint.
        return !!element && element instanceof joint.dia.Element && isLiteralFlowEndpoint(element);
    }

    private createToolsViewForElement(element: joint.dia.Element): joint.dia.ToolsView | null {
        // Offer tools for Nodes or resizable elements; ignore everything else on hover.
        const supportsMenu = element instanceof Node;
        const isResizable = this.isElementResizable(element);
        const sourceSelectionElement = this.getCurrentDragOrMoveFunctionSourceElement();
        if (!supportsMenu && !isResizable) {
            return null;
        }

        const tools: Array<joint.elementTools.Button | joint.elementTools.Boundary> = [];

        if (!this.isCellInGroupSelection(element) && sourceSelectionElement !== element && this.isGroupSelectableCell(element)) {
            tools.push(new joint.elementTools.Boundary({ padding: 2, focusOpacity: 0.75 }));
        }
        
        // tools.push(this.createMenuControlButton('calc(w)', 0)); // Replaced by floating entity action bar on selection

        if (isResizable) {
            tools.push(...this.createResizeHandles());
        }

        return new joint.dia.ToolsView({ tools });
    }

    private createGroupSelectionTools(): joint.dia.ToolsView | null {
        if (!this.groupSelection) {
            return null;
        }

        const children = this.getGroupSelectionChildren();
        if (children.length < 2) {
            return null;
        }

        const tools = [
            this.createGroupSelectionButton(12, 'calc(0.5*h)', '←', 'Align Left', () => this.alignGroupSelection('left')),
            this.createGroupSelectionButton('calc(0.5*w)', 12, '↑', 'Align Top', () => this.alignGroupSelection('top')),
            this.createGroupSelectionButton('calc(w - 12)', 'calc(0.5*h)', '→', 'Align Right', () => this.alignGroupSelection('right')),
            this.createGroupSelectionButton('calc(0.5*w)', 'calc(h - 12)', '↓', 'Align Bottom', () => this.alignGroupSelection('bottom')),
            this.createGroupSelectionButton('calc(0.5*w - 14)', 'calc(0.5*h - 14)', '↕', 'Arrange Vertical Line', () => this.arrangeGroupSelection('vertical')),
            this.createGroupSelectionButton('calc(0.5*w + 14)', 'calc(0.5*h - 14)', '↔', 'Arrange Horizontal Line', () => this.arrangeGroupSelection('horizontal')),
            this.createGroupSelectionButton('calc(0.5*w)', 'calc(0.5*h + 14)', '⋮⋮⋮', 'Arrange Grid', () => this.arrangeGroupSelection('grid')),
        ];

        return new joint.dia.ToolsView({ tools });
    }

    private createGroupSelectionButton(x: number | string, y: number | string, label: string, title: string, onInvoke: () => void): joint.elementTools.Button {
        return new joint.elementTools.Button({
            markup: [
                {
                    tagName: 'title',
                    selector: 'title',
                    textContent: title,
                },
                {
                    tagName: 'rect',
                    selector: 'button',
                    attributes: {
                        width: 24,
                        height: 24,
                        x: -12,
                        y: -12,
                        rx: 4,
                        ry: 4,
                        fill: '#FFFFFF',
                        stroke: '#00a6ff',
                        strokeWidth: 1,
                        cursor: 'pointer',
                    },
                },
                {
                    tagName: 'text',
                    selector: 'label',
                    attributes: {
                        x: 0,
                        y: 0,
                        fontSize: 12,
                        fontFamily: '"Open Sans", sans-serif',
                        fontWeight: 'bold',
                        textAnchor: 'middle',
                        textVerticalAnchor: 'middle',
                        'alignment-baseline': 'middle',
                        'dominant-baseline': 'middle',
                        fill: '#00a6ff',
                        cursor: 'pointer',
                    },
                    textContent: label,
                },
            ],
            x,
            y,
            selector: 'body',
            useModelGeometry: true,
            action: (evt) => {
                evt.preventDefault();
                evt.stopPropagation();
                onInvoke();
            },
        });
    }

    private createResizeHandles(): joint.elementTools.Button[] {
        return [
            this.createResizeButton('topLeft', -2, -2, 'nwse-resize'),
            this.createResizeButton('top', 'calc(0.5*w)', -2, 'ns-resize'),
            this.createResizeButton('topRight', 'calc(w + 2)', -2, 'nesw-resize'),
            this.createResizeButton('right', 'calc(w + 2)', 'calc(0.5*h)', 'ew-resize'),
            this.createResizeButton('bottomRight', 'calc(w + 2)', 'calc(h + 2)', 'nwse-resize'),
            this.createResizeButton('bottom', 'calc(0.5*w)', 'calc(h + 2)', 'ns-resize'),
            this.createResizeButton('bottomLeft', -2, 'calc(h + 2)', 'nesw-resize'),
            this.createResizeButton('left', -2, 'calc(0.5*h)', 'ew-resize'),
        ];
    }

    private createResizeButton(direction: ResizeDirection, x: number | string, y: number | string, cursor: string): joint.elementTools.Button {
        return new joint.elementTools.Button({
            markup: [{
                tagName: 'rect',
                selector: 'button',
                attributes: {
                    width: 10,
                    height: 10,
                    x: -5,
                    y: -5,
                    fill: '#FFFFFF',
                    stroke: '#00a6ff',
                    strokeWidth: 1,
                    cursor: cursor,
                    'data-resize-handle': 'true',
                }
            }],
            x,
            y,
            selector: 'body',
            useModelGeometry: true,
            action: (evt, elementView) => this.startElementResize(evt, elementView, direction),
        });
    }

    private createMenuControlButton(x: number | string, y: number | string): joint.elementTools.Button {
        return new joint.elementTools.Button({
            markup: [
                {
                    tagName: 'rect',
                    selector: 'body',
                    attributes: {
                        x: -10,
                        y: -10,
                        width: 40,
                        height: 40,
                        stroke: 'none',
                        fill: 'transparent',
                    }
                },
                {
                    tagName: 'circle',
                    selector: 'hitArea',
                    attributes: {
                        cx: 15,
                        cy: -15,
                        r: 15,
                        fill: '#FFFFFF',
                        stroke: '#CECECE',
                        strokeWidth: 1,
                        cursor: 'pointer',
                    }
                },
                {
                    tagName: 'text',
                    selector: 'label',
                    attributes: {
                        x: 15,
                        y: -7,
                        width: 30,
                        height: 30,
                        fontSize: 26,
                        fontWeight: 'bold',
                        textAnchor: 'middle',
                        textVerticalAnchor: 'middle',
                        fill: '#OOOOOO',
                        cursor: 'pointer',
                    },
                    textContent: '⋮', // Unicode vertical ellipsis character
                },
            ],
            x,
            y,
            selector: 'body',
            useModelGeometry: true,
            action: async (evt, elementView) => {
                this.closeMenus();
                await this.openMenu(elementView.model, evt.pageX, evt.pageY);
            },
        });
    }

    private startElementResize(evt: joint.dia.Event, elementView: joint.dia.ElementView, direction: ResizeDirection) {
        evt.preventDefault();
        evt.stopPropagation();

        // Cancel any pan that might have started from blank:pointerdown firing before this action
        this.panStartPosition = null;

        const model = elementView.model;
        if (!this.isElementResizable(model)) {
            return;
        }

        const clientPoint = this.getClientCoordinatesFromEvent(evt);
        if (!clientPoint) {
            return;
        }

        const pointer = this.paper.clientToLocalPoint(clientPoint);
        const startSize = model.size();
        const startPosition = model.position();
        const minSize = this.getElementMinResizeSize(model);

        const elementStartPosition = { x: startPosition.x, y: startPosition.y };
        const elementStartSize = { width: startSize.width, height: startSize.height };
        const elementOriginalRight = elementStartPosition.x + elementStartSize.width;
        const elementOriginalBottom = elementStartPosition.y + elementStartSize.height;

        this.resizeState = {
            element: model,
            elementView,
            direction,
            startPointer: { x: pointer.x, y: pointer.y },
            elementStartPosition,
            elementStartSize,
            elementOriginalRight,
            elementOriginalBottom,
            minSize,
        };

        // Mark element as being resized (used by Callout to preserve tail tip position)
        if (model instanceof Callout) {
            model.prop('isResizing', true);
        }

        document.addEventListener('pointermove', this.resizeMoveHandler);
        document.addEventListener('pointerup', this.resizeEndHandler);
        document.addEventListener('pointercancel', this.resizeEndHandler);
        document.body.style.cursor = this.cursorForResizeDirection(direction);

        // close any open menus
        this.closeMenus();
    }

    private snapToGrid(value: number): number {
        const gridSize = this.paper?.options?.gridSize as number || 15;
        return Math.round(value / gridSize) * gridSize;
    }

    private onElementResizeMove(evt: PointerEvent) {
        if (!this.resizeState) {
            return;
        }

        evt.preventDefault();
        evt.stopPropagation();

        const pointer = this.paper.clientToLocalPoint({ x: evt.clientX, y: evt.clientY });
        const dx = pointer.x - this.resizeState.startPointer.x;
        const dy = pointer.y - this.resizeState.startPointer.y;

        const affectsLeft = this.resizeState.direction === 'left'
            || this.resizeState.direction === 'topLeft'
            || this.resizeState.direction === 'bottomLeft';
        const affectsRight = this.resizeState.direction === 'right'
            || this.resizeState.direction === 'topRight'
            || this.resizeState.direction === 'bottomRight';
        const affectsTop = this.resizeState.direction === 'top'
            || this.resizeState.direction === 'topLeft'
            || this.resizeState.direction === 'topRight';
        const affectsBottom = this.resizeState.direction === 'bottom'
            || this.resizeState.direction === 'bottomLeft'
            || this.resizeState.direction === 'bottomRight';

        // Work with element edges directly (not body edges) to avoid offset issues
        let newElementLeft = this.resizeState.elementStartPosition.x;
        let newElementRight = this.resizeState.elementOriginalRight;
        let newElementTop = this.resizeState.elementStartPosition.y;
        let newElementBottom = this.resizeState.elementOriginalBottom;

        // Snap each affected edge to the grid
        if (affectsLeft) {
            const proposedLeft = this.resizeState.elementStartPosition.x + dx;
            newElementLeft = this.snapToGrid(proposedLeft);
            // Enforce minimum width
            if (newElementRight - newElementLeft < this.resizeState.minSize.width) {
                newElementLeft = newElementRight - this.resizeState.minSize.width;
            }
        }

        if (affectsRight) {
            const proposedRight = this.resizeState.elementOriginalRight + dx;
            newElementRight = this.snapToGrid(proposedRight);
            // Enforce minimum width
            if (newElementRight - newElementLeft < this.resizeState.minSize.width) {
                newElementRight = newElementLeft + this.resizeState.minSize.width;
            }
        }

        if (affectsTop) {
            const proposedTop = this.resizeState.elementStartPosition.y + dy;
            newElementTop = this.snapToGrid(proposedTop);
            // Enforce minimum height
            if (newElementBottom - newElementTop < this.resizeState.minSize.height) {
                newElementTop = newElementBottom - this.resizeState.minSize.height;
            }
        }

        if (affectsBottom) {
            const proposedBottom = this.resizeState.elementOriginalBottom + dy;
            newElementBottom = this.snapToGrid(proposedBottom);
            // Enforce minimum height
            if (newElementBottom - newElementTop < this.resizeState.minSize.height) {
                newElementBottom = newElementTop + this.resizeState.minSize.height;
            }
        }

        const newElementX = newElementLeft;
        const newElementY = newElementTop;
        const newElementWidth = newElementRight - newElementLeft;
        const newElementHeight = newElementBottom - newElementTop;

        this.resizeState.element.position(newElementX, newElementY);
        this.resizeState.element.resize(newElementWidth, newElementHeight);
        
        if (this.isCellInGroupSelection(this.resizeState.element)) {
            this.updateGroupSelectionBounds();
        }
    }

    private onElementResizeEnd(evt: PointerEvent) {
        if (!this.resizeState) {
            return;
        }

        evt.preventDefault();

        const { element, elementView } = this.resizeState;
        const stillHovering = this.isPointerInsideCell(evt, element);

        // Clear resize flag (used by Callout to preserve tail tip position)
        if (element instanceof Callout) {
            element.prop('isResizing', false);
        }

        document.removeEventListener('pointermove', this.resizeMoveHandler);
        document.removeEventListener('pointerup', this.resizeEndHandler);
        document.removeEventListener('pointercancel', this.resizeEndHandler);
        document.body.style.cursor = '';

        this.resizeState = null;

        if (!stillHovering) {
            elementView.removeTools();
        }
    }

    private beginCalloutTailDrag(evt: joint.dia.Event, callout: Callout) {
        const clientPoint = this.getClientCoordinatesFromEvent(evt);
        if (!clientPoint) {
            return;
        }

        const paperPoint = this.paper.clientToLocalPoint(clientPoint);
        this.updateCalloutTailFromPaperPoint(callout, paperPoint);

        this.closeMenus();
        this.resetToolMode();
        this.cancelCalloutTailDrag();

        callout.setTailHandleCursor('grabbing');
        callout.showTailHandle();

        this.calloutTailDragState = {
            callout,
        };

        document.addEventListener('pointermove', this.calloutTailMoveHandler);
        document.addEventListener('pointerup', this.calloutTailEndHandler);
        document.addEventListener('pointercancel', this.calloutTailEndHandler);
    }

    private onCalloutTailMove(evt: PointerEvent) {
        if (!this.calloutTailDragState) {
            return;
        }

        evt.preventDefault();

        const paperPoint = this.paper.clientToLocalPoint({ x: evt.clientX, y: evt.clientY });
        this.updateCalloutTailFromPaperPoint(this.calloutTailDragState.callout, paperPoint);
        this.updateGroupSelectionBounds();
    }

    private onCalloutTailEnd(evt: PointerEvent) {
        if (!this.calloutTailDragState) {
            return;
        }

        evt.preventDefault();

        this.calloutTailDragState.callout.setTailHandleCursor('grab');
        this.cancelCalloutTailDrag();
    }

    private cancelCalloutTailDrag() {
        if (!this.calloutTailDragState) {
            return;
        }

        document.removeEventListener('pointermove', this.calloutTailMoveHandler);
        document.removeEventListener('pointerup', this.calloutTailEndHandler);
        document.removeEventListener('pointercancel', this.calloutTailEndHandler);

        this.calloutTailDragState.callout.setTailHandleCursor('grab');
        this.calloutTailDragState = null;
    }

    // Grouped drag (move callout + tail together) using tail body drag
    
    // A cell is "group-selectable" if it can be directly selected by the user for group operations
    // This includes: Elements (except GroupSelection) and DrawnLines
    private isGroupSelectableCell(cell: joint.dia.Cell): boolean {
        if (cell instanceof GroupSelection) return false;
        if (cell instanceof DrawnLine) return true;
        if (cell instanceof joint.dia.Element) return true;
        return false;
    }

    // Links that are selectable but not group-selectable (all links except DrawnLine)
    private isNonGroupSelectableLink(cell: joint.dia.Cell): cell is joint.shapes.standard.Link {
        return cell instanceof joint.dia.Link && !(cell instanceof DrawnLine);
    }

    // A link is "auto-embeddable" if it connects two selected elements (not DrawnLine)
    private isLinkAutoEmbeddable(link: joint.dia.Link): boolean {
        return !(link instanceof DrawnLine);
    }

    private getSelectableCellBoundingBox(cell: joint.dia.Cell): Rect | null {
        if (cell instanceof Callout) {
            return cell.getFullBBox();
        }

        const bbox = cell.getBBox() as Rect | undefined;
        return bbox ?? null;
    }

    // True when a link renders nothing: a same-node collapse hides it via root/display and the flow
    // legend hides a whole flow type via line/display. Either way its (stale) routing geometry must
    // not count toward a group selection's bounding box.
    private isLinkGeometryHidden(link: joint.dia.Link): boolean {
        return link.attr('root/display') === 'none' || link.attr('line/display') === 'none';
    }

    private isCellInGroupSelection(cell: joint.dia.Cell): boolean {
        if (!this.groupSelection) {
            return false;
        }

        if (cell.get('parent') === this.groupSelection.id) {
            return true;
        }

        return this.groupSelection
            .getEmbeddedCells({ deep: false })
            .some(embedded => embedded.id === cell.id);
    }
    
    private isOnlyChildOfGroupSelection(cell: joint.dia.Cell): boolean {
        if (!this.groupSelection) return false;
        
        const children = this.getGroupSelectionChildren();
        return children.length === 1 && children[0] === cell;
    }

    private addCellToGroupSelection(cell: joint.dia.Cell): void {
        if (!this.groupSelection) return;
        if (!this.isGroupSelectableCell(cell)) return;
        if (this.isCellInGroupSelection(cell)) return;
        
        this.groupSelection.embed(cell);
        
        // if more than one child exists, remove tools and apply selection highlight to all
        if (this.getGroupSelectionChildren().length > 1) {
            this.removeTools();
            this.getGroupSelectionChildren().forEach(child => {
                this.applySelectionHighlight(child);
            });
        }
        
        this.updateGroupSelectionLinks();
        this.updateGroupSelectionBounds();
    }
    
    private removeCellFromGroupSelection(cell: joint.dia.Cell): void {
        if (!this.groupSelection) return;
        if (!this.isCellInGroupSelection(cell)) return;

        this.removeSelectionHighlight(cell);
        this.groupSelection.unembed(cell);
        
        // if only one child remains, remove selection highlight from it
        const children = this.getGroupSelectionChildren();
        if (children.length === 1) {
            this.removeSelectionHighlight(children[0]);
        }
        
        this.updateGroupSelectionLinks();
        this.updateGroupSelectionBounds();
    }

    private applySelectionHighlight(cell: joint.dia.Cell): void {
        if (!this.isGroupSelectableCell(cell)) return;
        
        const view = this.paper.findViewByModel(cell);
        if (!view) return;
        
        const el = view.el as SVGElement;
        el.classList.add('group-selected');
    }

    private removeSelectionHighlight(cell: joint.dia.Cell): void {
        if (!this.isGroupSelectableCell(cell)) return;
        
        const view = this.paper.findViewByModel(cell);
        if (!view) return;
        
        const el = view.el as SVGElement;
        el.classList.remove('group-selected');
    }

    private handleGroupSelectionToggle(cell: joint.dia.Cell): void {
        // Ensure group selection exists
        if (!this.groupSelection) {
            this.groupSelection = new GroupSelection();
            this.graph.addCell(this.groupSelection);
        }

        if (this.isCellInGroupSelection(cell)) {
            this.removeCellFromGroupSelection(cell);
        } else {
            this.addCellToGroupSelection(cell);
        }
    }

    private handleGroupSelectableClick(cell: joint.dia.Cell, isShiftPressed: boolean): boolean {
        if (!this.isGroupSelectableCell(cell)) {
            return false;
        }

        this.clearLinkSelection();

        if (!isShiftPressed) {
            // Single click: clear group if multiple items or if this cell isn't in it
            if (this.getGroupSelectionChildren().length > 1 || !this.isCellInGroupSelection(cell)) {
                this.removeGroupSelection();
            }
        }

        this.handleGroupSelectionToggle(cell);
        this.notifyAndUpdateToolsForGroupSelection();
        return true;
    }

    // Notify C# about the current group selection state and apply appropriate tools
    private notifyAndUpdateToolsForGroupSelection(): void {
        const children = this.getGroupSelectionChildren();
        
        if (children.length === 0) {
            this.clearLinkSelection();
            this.closePropertiesPanel();
            return;
        }
        
        if (children.length === 1) {
            const single = children[0];
            if (single instanceof DrawnLine) {
                const linkView = single.findView(this.paper);
                if (linkView && !linkView.hasTools()) {
                    linkView.addTools(this.buildLinkTools(single));
                }
                this.notifyGroupSelection();
            } else if (single instanceof joint.dia.Element) {
                this.clearLinkSelection();
                const view = this.paper.findViewByModel(single) as joint.dia.ElementView | null;
                const toolsView = this.createToolsViewForElement(single);
                if (toolsView && view) {
                    this.removeTools();
                    if (single instanceof Callout) {
                        single.showTailHandle();
                    }
                    view.addTools(toolsView);
                }
                this.notifyElementSelection(single);
            }
            return;
        }
        
        // Multiple items selected
        this.clearLinkSelection();
        this.notifyGroupSelection();
    }

    // Returns all directly selected cells (Elements and DrawnLines, not auto-embedded links)
    private getGroupSelectionChildren(): joint.dia.Cell[] {
        if (!this.groupSelection) {
            return [];
        }
        return this.groupSelection
            .getEmbeddedCells({ deep: false })
            .filter(cell => this.isGroupSelectableCell(cell));
    }

    // Returns all embedded cells including auto-embedded links
    private getGroupSelectionEmbeddedCells(): joint.dia.Cell[] {
        if (!this.groupSelection) {
            return [];
        }
        return this.groupSelection.getEmbeddedCells({ deep: false });
    }

    // Auto-embed links that connect two selected elements (not DrawnLines)
    private updateGroupSelectionLinks(): void {
        if (!this.groupSelection) {
            return;
        }

        const children = this.getGroupSelectionChildren();
        const selectedElementIds = new Set(
            children
                .filter(cell => cell instanceof joint.dia.Element)
                .map(cell => String(cell.id))
        );

        // Find links that connect two selected elements
        const internalLinks = this.graph.getLinks().filter(link => {
            if (!this.isLinkAutoEmbeddable(link)) return false;
            const sourceId = link.get('source')?.id;
            const targetId = link.get('target')?.id;
            if (!sourceId || !targetId) return false;
            return selectedElementIds.has(String(sourceId)) && selectedElementIds.has(String(targetId));
        });

        const internalLinkIds = new Set(internalLinks.map(link => String(link.id)));
        const childIds = new Set(children.map(cell => String(cell.id)));
        
        // Get all embedded links
        const embeddedLinks = this.getGroupSelectionEmbeddedCells()
            .filter(cell => cell instanceof joint.dia.Link) as joint.dia.Link[];

        // Unembed links that are no longer internal (but not manually selected DrawnLines)
        embeddedLinks.forEach(link => {
            // Keep manually selected cells (DrawnLines)
            if (childIds.has(String(link.id))) return;
            // Unembed if no longer an internal link
            if (!internalLinkIds.has(String(link.id))) {
                this.groupSelection.unembed(link);
            }
        });

        // Embed internal links
        internalLinks.forEach(link => {
            if (link.get('parent') !== this.groupSelection!.id) {
                this.groupSelection.embed(link);
            }
        });
    }

    private updateGroupSelectionBounds(): void {
        if (!this.groupSelection) {
            return;
        }

        const children = this.getGroupSelectionChildren();
        if (children.length === 0) {
            this.removeGroupSelection();
            return;
        }

        const embeddedCells = this.getGroupSelectionEmbeddedCells();

        // unembed all cells, then reposition the groupSelection, then re-embed again
        embeddedCells.forEach(cell => {
            this.groupSelection.unembed(cell);
        });

        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;

        // calculate bounding box of all embedded cells
        embeddedCells.forEach(cell => {
            // A hidden link contributes no visible geometry but keeps its stale routing vertices, so
            // counting its bbox would stretch the selection box out to empty space. Skip links hidden
            // by a same-node collapse (root/display, see updateSameNodeLinkVisibility) or by a flow
            // type switched off in the legend (line/display, see toggleFlowElementVisibility).
            if (cell instanceof joint.dia.Link && this.isLinkGeometryHidden(cell)) {
                return;
            }
            const bbox = this.getSelectableCellBoundingBox(cell) ?? (cell.getBBox() as Rect);
            if (bbox) {
                minX = Math.min(minX, bbox.x);
                minY = Math.min(minY, bbox.y);
                maxX = Math.max(maxX, bbox.x + bbox.width);
                maxY = Math.max(maxY, bbox.y + bbox.height);
            }
        });
        
        const newX = minX;
        const newY = minY;
        const newWidth = Math.max(1, (maxX - minX) + GroupSelection.Padding + GroupSelection.StrokeWidth);
        const newHeight = Math.max(1, (maxY - minY) + GroupSelection.Padding + GroupSelection.StrokeWidth);
        
        // re-embed selectable children first
        children.forEach(child => {
            this.groupSelection.embed(child);
        });

        // then auto-embed internal links
        this.updateGroupSelectionLinks();

        this.groupSelection.position(newX, newY);
        this.groupSelection.resize(newWidth, newHeight);
        
        if (children.length === 1) {
            children[0].toFront();
        } else {
            // if more than one item in the group, GroupSelection should be in front to intercept pointer events
            this.groupSelection.toFront();
        }
    }

    private getCellBoundingBox(cell: joint.dia.Cell): Rect | null {
        if (!(cell instanceof joint.dia.Element)) {
            return null;
        }

        if (cell instanceof Callout) {
            return cell.getFullBBox();
        }

        const bbox = cell.getBBox() as Rect | undefined;
        if (!bbox) {
            return null;
        }

        return {
            x: bbox.x,
            y: bbox.y,
            width: bbox.width,
            height: bbox.height,
        };
    }

    private getLayoutItems(cells: joint.dia.Cell[]): LayoutItem[] {
        const items: LayoutItem[] = [];

        cells.forEach(cell => {
            if (!(cell instanceof joint.dia.Element)) {
                return;
            }

            const bbox = this.getCellBoundingBox(cell);
            if (!bbox) {
                return;
            }

            const position = cell.position();
            items.push({
                element: cell,
                bbox,
                offsetX: bbox.x - position.x,
                offsetY: bbox.y - position.y,
            });
        });

        return items;
    }

    private alignGroupSelection(direction: AlignmentDirection): void {
        if (!this.groupSelection) {
            return;
        }

        const children = this.getGroupSelectionChildren();
        if (children.length < 2) {
            return;
        }

        const groupPosition = this.groupSelection.position();
        const groupSize = this.groupSelection.size();
        const padding = GroupSelection.Padding;

        const targetLeft = groupPosition.x + padding;
        const targetTop = groupPosition.y + padding;
        const targetRight = groupPosition.x + groupSize.width - padding;
        const targetBottom = groupPosition.y + groupSize.height - padding;

        const items = this.getLayoutItems(children);
        if (items.length === 0) {
            return;
        }

        this.graph.startBatch('group-selection-align');

        items.forEach(item => {
            const currentPosition = item.element.position();
            let newX = currentPosition.x;
            let newY = currentPosition.y;

            switch (direction) {
                case 'left':
                    newX = targetLeft - item.offsetX;
                    break;
                case 'right':
                    newX = targetRight - item.bbox.width - item.offsetX;
                    break;
                case 'top':
                    newY = targetTop - item.offsetY;
                    break;
                case 'bottom':
                    newY = targetBottom - item.bbox.height - item.offsetY;
                    break;
            }

            if (newX !== currentPosition.x || newY !== currentPosition.y) {
                item.element.position(newX, newY);
            }
        });

        this.graph.stopBatch('group-selection-align');
        this.updateGroupSelectionBounds();
    }

    private arrangeGroupSelection(layout: LayoutFormation): void {
        if (!this.groupSelection) {
            return;
        }

        const children = this.getGroupSelectionChildren();
        if (children.length < 2) {
            return;
        }

        const items = this.getLayoutItems(children);
        if (items.length < 2) {
            return;
        }

        const groupPosition = this.groupSelection.position();
        const groupSize = this.groupSelection.size();
        const centerX = groupPosition.x + groupSize.width / 2;
        const centerY = groupPosition.y + groupSize.height / 2;
        const gap = this.groupLayoutGap;

        this.graph.startBatch('group-selection-layout');

        if (layout === 'vertical') {
            const sorted = [...items].sort((a, b) => a.bbox.y - b.bbox.y);
            const totalHeight = sorted.reduce((sum, item) => sum + item.bbox.height, 0);
            const totalSpacing = gap * (sorted.length - 1);
            let currentY = centerY - (totalHeight + totalSpacing) / 2;

            sorted.forEach(item => {
                const targetX = centerX - item.bbox.width / 2;
                const targetY = currentY;
                item.element.position(targetX - item.offsetX, targetY - item.offsetY);
                currentY += item.bbox.height + gap;
            });
        } else if (layout === 'horizontal') {
            const sorted = [...items].sort((a, b) => a.bbox.x - b.bbox.x);
            const totalWidth = sorted.reduce((sum, item) => sum + item.bbox.width, 0);
            const totalSpacing = gap * (sorted.length - 1);
            let currentX = centerX - (totalWidth + totalSpacing) / 2;

            sorted.forEach(item => {
                const targetX = currentX;
                const targetY = centerY - item.bbox.height / 2;
                item.element.position(targetX - item.offsetX, targetY - item.offsetY);
                currentX += item.bbox.width + gap;
            });
        } else if (layout === 'grid') {
            const sorted = [...items].sort((a, b) => {
                if (a.bbox.y === b.bbox.y) {
                    return a.bbox.x - b.bbox.x;
                }
                return a.bbox.y - b.bbox.y;
            });

            const count = sorted.length;
            let bestCols = Math.ceil(Math.sqrt(count));
            let bestRows = Math.ceil(count / bestCols);
            let bestScore = Math.abs(bestCols - bestRows);
            let bestArea = bestCols * bestRows;

            for (let colsCandidate = 1; colsCandidate <= count; colsCandidate++) {
                const rowsCandidate = Math.ceil(count / colsCandidate);
                const area = colsCandidate * rowsCandidate;
                if (area < count) {
                    continue;
                }

                const score = Math.abs(colsCandidate - rowsCandidate);
                if (score < bestScore || (score === bestScore && area < bestArea)) {
                    bestCols = colsCandidate;
                    bestRows = rowsCandidate;
                    bestScore = score;
                    bestArea = area;
                }
            }

            const rowHeights = new Array(bestRows).fill(0);
            const colWidths = new Array(bestCols).fill(0);

            sorted.forEach((item, index) => {
                const row = Math.floor(index / bestCols);
                const col = index % bestCols;
                rowHeights[row] = Math.max(rowHeights[row], item.bbox.height);
                colWidths[col] = Math.max(colWidths[col], item.bbox.width);
            });

            const totalWidth = colWidths.reduce((sum, width) => sum + width, 0) + gap * (bestCols - 1);
            const totalHeight = rowHeights.reduce((sum, height) => sum + height, 0) + gap * (bestRows - 1);

            const startX = centerX - totalWidth / 2;
            const startY = centerY - totalHeight / 2;

            const colOffsets: number[] = [];
            let currentColumnX = startX;
            for (let col = 0; col < bestCols; col++) {
                colOffsets.push(currentColumnX);
                currentColumnX += colWidths[col] + gap;
            }

            const rowOffsets: number[] = [];
            let currentRowY = startY;
            for (let row = 0; row < bestRows; row++) {
                rowOffsets.push(currentRowY);
                currentRowY += rowHeights[row] + gap;
            }

            sorted.forEach((item, index) => {
                const row = Math.floor(index / bestCols);
                const col = index % bestCols;

                const cellWidth = colWidths[col];
                const cellHeight = rowHeights[row];
                const targetX = colOffsets[col] + (cellWidth - item.bbox.width) / 2;
                const targetY = rowOffsets[row] + (cellHeight - item.bbox.height) / 2;

                item.element.position(targetX - item.offsetX, targetY - item.offsetY);
            });
        }

        this.graph.stopBatch('group-selection-layout');
        this.updateGroupSelectionBounds();
    }

    private removeGroupSelection(): void {
        if (!this.groupSelection) {
            return;
        }

        const group = this.groupSelection;
        const embeddedCells = this.getGroupSelectionEmbeddedCells();
        const elementChildren = this.getGroupSelectionChildren();
        elementChildren.forEach(child => this.removeSelectionHighlight(child));
        embeddedCells.forEach(cell => group.unembed(cell));

        this.groupSelection = null;

        group.remove();
        this.closePropertiesPanel();
    }

    private beginShiftGroupSelection(x: number, y: number): void {
        this.removeGroupSelection();
        this.removeTools();

        this.removeShiftSelectionHandlers();

        this.shiftSelectionStart = { x, y };
        this.panStartPosition = null;

        document.addEventListener('pointermove', this.shiftSelectionMoveHandler);
        document.addEventListener('pointerup', this.shiftSelectionEndHandler);
        document.addEventListener('pointercancel', this.shiftSelectionEndHandler);
    }

    private updateShiftGroupSelection(x: number, y: number): void {
        if (!this.shiftSelectionStart) {
            return;
        }

        if (!this.tempGroupSelectionElement) {
            const gridSize = this.paper?.options?.gridSize as number || 15;
            const start = this.shiftSelectionStart;
            const deltaX = Math.abs(x - start.x);
            const deltaY = Math.abs(y - start.y);
            const distance = Math.max(deltaX, deltaY);
            if (distance < gridSize) {
                return;
            }

            this.tempGroupSelectionElement = new GroupSelection();
            this.tempGroupSelectionElement.attr('body/strokeDasharray', '4 4');
            this.tempGroupSelectionElement.attr('body/cursor', 'default');
            this.tempGroupSelectionElement.attr('body/pointer-events', 'none');
            this.tempGroupSelectionElement.position(start.x + GroupSelection.Padding + GroupSelection.StrokeWidth, start.y + GroupSelection.Padding + GroupSelection.StrokeWidth);
            this.tempGroupSelectionElement.resize(1, 1);
            this.graph.addCell(this.tempGroupSelectionElement);
            this.tempGroupSelectionElement.toFront();
        }

        const start = this.shiftSelectionStart;
        const minX = Math.min(start.x, x);
        const minY = Math.min(start.y, y);
        const width = Math.max(1, Math.abs(x - start.x));
        const height = Math.max(1, Math.abs(y - start.y));

        this.tempGroupSelectionElement.position(minX + GroupSelection.Padding + GroupSelection.StrokeWidth, minY + GroupSelection.Padding + GroupSelection.StrokeWidth);
        this.tempGroupSelectionElement.resize(width - GroupSelection.Padding - GroupSelection.StrokeWidth, height - GroupSelection.Padding - GroupSelection.StrokeWidth);
        this.tempGroupSelectionElement.toFront();
    }

    private onShiftSelectionMove(evt: PointerEvent): void {
        if (!this.shiftSelectionStart) {
            return;
        }

        evt.preventDefault();

        const pointer = { x: evt.clientX, y: evt.clientY };
        const localPoint = this.paper.clientToLocalPoint(pointer);
        this.updateShiftGroupSelection(localPoint.x, localPoint.y);
    }

    private onShiftSelectionEnd(evt: PointerEvent): void {
        if (!this.shiftSelectionStart) {
            this.removeShiftSelectionHandlers();
            return;
        }

        evt.preventDefault();

        const pointer = { x: evt.clientX, y: evt.clientY };
        const localPoint = this.paper.clientToLocalPoint(pointer);
        this.updateShiftGroupSelection(localPoint.x, localPoint.y);
        this.completeShiftGroupSelection(localPoint);
    }

    private completeShiftGroupSelection(endPoint: { x: number; y: number; } | null): void {
        const start = this.shiftSelectionStart;
        const preview = this.tempGroupSelectionElement;

        this.shiftSelectionStart = null;
        this.removeShiftSelectionHandlers();

        if (preview) {
            preview.remove();
            this.tempGroupSelectionElement = null;
        }

        if (!start || !endPoint) {
            return;
        }

        const deltaX = Math.abs(endPoint.x - start.x);
        const deltaY = Math.abs(endPoint.y - start.y);
        if (deltaX < 2 && deltaY < 2) {
            return;
        }

        const selectionRect = this.createRectFromPoints(start, endPoint);

        // Find all selectable cells (elements and DrawnLines) that intersect the selection rectangle
        const allCells: joint.dia.Cell[] = [
            ...this.graph.getElements(),
            ...this.graph.getLinks()
        ];
        
        const cellsToSelect = allCells.filter(cell => {
            if (!this.isGroupSelectableCell(cell)) return false;
            
            const bbox = this.getSelectableCellBoundingBox(cell);
            if (!bbox) return false;
            
            return this.rectsIntersect(selectionRect, bbox);
        });

        if (cellsToSelect.length === 0) {
            return;
        }

        this.clearLinkSelection();
        this.removeGroupSelection();

        this.groupSelection = new GroupSelection();
        this.graph.addCell(this.groupSelection);

        this.graph.startBatch('group-selection-shift-drag');
        cellsToSelect.forEach(cell => {
            this.addCellToGroupSelection(cell);
        });
        this.graph.stopBatch('group-selection-shift-drag');

        this.updateGroupSelectionBounds();

        this.removeTools();

        if (cellsToSelect.length === 1) {
            const single = cellsToSelect[0];
            if (single instanceof joint.dia.Element) {
                const elementView = this.paper.findViewByModel(single) as joint.dia.ElementView | null;
                const toolsView = this.createToolsViewForElement(single);
                if (toolsView && elementView) {
                    if (single instanceof Callout) {
                        single.showTailHandle();
                    }
                    elementView.addTools(toolsView);
                }
                // Notify C# about the single element selection
                this.notifyElementSelection(single);
            } else if (single instanceof DrawnLine) {
                this.notifyGroupSelection();
            }
        } else {
            // Notify C# about the group selection
            this.notifyGroupSelection();
        }
    }

    private removeShiftSelectionHandlers(): void {
        document.removeEventListener('pointermove', this.shiftSelectionMoveHandler);
        document.removeEventListener('pointerup', this.shiftSelectionEndHandler);
        document.removeEventListener('pointercancel', this.shiftSelectionEndHandler);
    }

    private createRectFromPoints(start: { x: number; y: number; }, end: { x: number; y: number; }): Rect {
        const minX = Math.min(start.x, end.x);
        const minY = Math.min(start.y, end.y);
        const width = Math.max(1, Math.abs(end.x - start.x));
        const height = Math.max(1, Math.abs(end.y - start.y));

        return { x: minX, y: minY, width, height };
    }

    private rectsIntersect(a: Rect, b: Rect): boolean {
        return a.x < b.x + b.width
            && a.x + a.width > b.x
            && a.y < b.y + b.height
            && a.y + a.height > b.y;
    }

    private updateCalloutTailFromPaperPoint(callout: Callout, paperPoint: { x: number; y: number; }) {
        // Same graph-to-local conversion the headless authoring path uses, shared so a pointer drag
        // and an authored tailTip can never disagree about the coordinate space.
        callout.setTailTip(tailTipToLocal(callout, paperPoint));
    }
    private cursorForResizeDirection(direction: ResizeDirection): string {
        switch (direction) {
            case 'left':
            case 'right':
                return 'ew-resize';
            case 'top':
            case 'bottom':
                return 'ns-resize';
            case 'topLeft':
            case 'bottomRight':
                return 'nwse-resize';
            case 'topRight':
            case 'bottomLeft':
                return 'nesw-resize';
            default:
                return 'default';
        }
    }

    private getClientCoordinatesFromEvent(evt: joint.dia.Event): { x: number; y: number; } | null {
        const candidate = evt as unknown as PointerEvent;
        if (typeof candidate.clientX === 'number' && typeof candidate.clientY === 'number') {
            return { x: candidate.clientX, y: candidate.clientY };
        }

        const mouseEvent = evt as unknown as MouseEvent;
        if (typeof mouseEvent.clientX === 'number' && typeof mouseEvent.clientY === 'number') {
            return { x: mouseEvent.clientX, y: mouseEvent.clientY };
        }

        const touchEvent = evt as unknown as TouchEvent;
        if (touchEvent.touches && touchEvent.touches.length > 0) {
            return { x: touchEvent.touches[0].clientX, y: touchEvent.touches[0].clientY };
        }
        if (touchEvent.changedTouches && touchEvent.changedTouches.length > 0) {
            return { x: touchEvent.changedTouches[0].clientX, y: touchEvent.changedTouches[0].clientY };
        }

        return null;
    }

    private isPointerInsideCell(evt: PointerEvent, element: joint.dia.Cell): boolean {
        if (typeof evt.clientX !== 'number' || typeof evt.clientY !== 'number') {
            return false;
        }

        const pointer = this.paper.clientToLocalPoint({ x: evt.clientX, y: evt.clientY });
        const bbox = element.getBBox();

        if (!bbox) {
            return false;
        }

        return pointer.x >= bbox.x && pointer.x <= bbox.x + bbox.width
            && pointer.y >= bbox.y && pointer.y <= bbox.y + bbox.height;
    }

    private isCalloutTailHandle(target: EventTarget | null): boolean {
        if (!target) {
            return false;
        }

        if (target instanceof SVGElement) {
            if (target.getAttribute('data-tail-handle') === 'true') {
                return true;
            }
        }

        return false;
    }

    private isResizeHandle(target: EventTarget | null): boolean {
        if (!target) {
            return false;
        }

        if (target instanceof Element) {
            if (target.getAttribute('data-resize-handle') === 'true') {
                return true;
            }

            return target.closest('[data-resize-handle="true"]') !== null;
        }

        return false;
    }

    private isElementResizable(element: joint.dia.Element): boolean {
        if (element.get('resizable')) {
            return true;
        }

        if (element instanceof TextBox || element instanceof DrawnRectangle || element instanceof Callout) {
            element.set({ resizable: true });
            return true;
        }

        return false;
    }

    private getElementMinResizeSize(element: joint.dia.Element): { width: number; height: number; } {
        const minWidth = Number(element.get('minResizeWidth'));
        const minHeight = Number(element.get('minResizeHeight'));

        const width = Number.isFinite(minWidth) && minWidth > 0 ? minWidth : this.resizeDefaultMinSize.width;
        const height = Number.isFinite(minHeight) && minHeight > 0 ? minHeight : this.resizeDefaultMinSize.height;

        return { width, height };
    }

    public getGraphJson() {
        this.removeGroupSelection();
        var graphJson = JSON.stringify(this.graph?.toJSON());
        return graphJson;
    }

    public setGraphJson(graphJson: string) {
        this.closeMenus();
        this.removeGroupSelection();

        if (graphJson) {
            this.paper.model.fromJSON(JSON.parse(graphJson), { cellNamespace: this.namespace });
            // Apply migration/cleanup for backward compatibility
            applyGraphBackCompatAndCleanup(this.graph);
            this.paper.model.fromJSON(this.graph.toJSON(), { cellNamespace: this.namespace });

            this.resetZoom();
        } else {
            this.paper.model.clear();
        }
    }

    // Invoked by C#
    public async applyMutations(request: { mutations: MutationDescriptor[]; entityCatalog: EntityCatalogEntry[] }) {
        this.closeMenus();
        const selection = this.captureSelection();
        this.clearSelectionForGraphSnapshot();

        const initialSnapshot = {
            generation: this.graphGeneration,
            graphJson: JSON.stringify(this.graph.toJSON()),
        };
        let graphReplaced = false;
        const operationEpoch = this.operationEpoch;

        try {
            const result = await applyLiveGraphMutation({
                initialSnapshot,
                mutations: request.mutations,
                entityCatalog: request.entityCatalog,
                getGeneration: () => this.graphGeneration,
                operationIsCurrent: () => this.operationEpoch === operationEpoch,
                captureFreshSnapshot: () => {
                    // Keep retry snapshots free of editor-only selection cells.
                    this.clearSelectionForGraphSnapshot();
                    return {
                        generation: this.graphGeneration,
                        graphJson: JSON.stringify(this.graph.toJSON()),
                    };
                },
                commit: updatedGraphJson => {
                    try {
                        this.paper.model.fromJSON(JSON.parse(updatedGraphJson), { cellNamespace: this.namespace });
                    } catch (error) {
                        console.error('Failed to apply mutations to graph:', error);
                        throw new Error(`Invalid graph JSON from mutations: ${error instanceof Error ? error.message : String(error)}`);
                    }

                    graphReplaced = true;
                    this.restoreSelection(selection);
                },
            });
            if (result.abandoned && !graphReplaced) {
                this.restoreSelection(selection);
            }
            return result;
        } catch (error) {
            if (!graphReplaced) {
                this.restoreSelection(selection);
            }
            throw error;
        }
    }

    public invalidateOperationEpoch() {
        this.operationEpoch += 1;
        return this.operationEpoch;
    }

    private captureSelection(): { linkId: string | null; groupCellIds: string[] } {
        return {
            linkId: this.selectedLink ? String(this.selectedLink.id) : null,
            groupCellIds: this.getGroupSelectionChildren().map(cell => String(cell.id)),
        };
    }

    private clearSelectionForGraphSnapshot(): void {
        this.clearLinkSelection();
        this.removeGroupSelection();
        // Rebuilding the graph recreates every cell view, so clear the C# selection before any
        // awaited work. restoreSelection will reopen it only when the selected cell survives.
        this.closePropertiesPanel();
    }

    private restoreSelection(selection: { linkId: string | null; groupCellIds: string[] }): void {
        this.clearLinkSelection();
        this.removeGroupSelection();

        if (selection.linkId) {
            const link = this.graph.getCell(selection.linkId);
            if (link && this.isNonGroupSelectableLink(link)) {
                this.selectLink(link);
                return;
            }
        }

        const cells = selection.groupCellIds
            .map(id => this.graph.getCell(id))
            .filter((cell): cell is joint.dia.Cell => !!cell && this.isGroupSelectableCell(cell));

        if (cells.length === 0) {
            this.closePropertiesPanel();
            return;
        }

        this.groupSelection = new GroupSelection();
        this.graph.addCell(this.groupSelection);
        this.graph.startBatch('restore-group-selection');
        cells.forEach(cell => this.addCellToGroupSelection(cell));
        this.graph.stopBatch('restore-group-selection');
        this.notifyAndUpdateToolsForGroupSelection();
    }

    // Invoked by C#
    private setRectangleMode(value: boolean) {
        this.activeTool = value ? ToolMode.DrawRectangle : null;
        this.setToolCursorActive(value);
    }

    // Invoked by C#
    private setTextBoxMode(value: boolean) {
        this.activeTool = value ? ToolMode.DrawTextBox : null;
        this.setToolCursorActive(value);
    }

    // Invoked by C#
    private setCalloutMode(value: boolean) {
        this.activeTool = value ? ToolMode.DrawCallout : null;
        this.setToolCursorActive(value);
    }

    // Invoked by C#
    private setLineMode(value: boolean) {
        this.activeTool = value ? ToolMode.DrawLine : null;
        this.setToolCursorActive(value);
    }

    // Invoked by C#
    private setIconMode(value: boolean, iconName: string | null) {
        const iconToPlace = iconName ?? this.iconNameToPlace;

        this.activeTool = value ? ToolMode.DrawIcon : null;
        this.iconNameToPlace = value ? iconToPlace : null;
        this.setToolCursorActive(value);

        if (value && iconToPlace) {
            const { x, y } = this.getDefaultIconPlacement();
            this.placeIcon(x, y);
            this.resetToolMode();
            this.removeGroupSelection();
            this.removeTools();
            this.closePropertiesPanel();
        }
    }

    private getDefaultIconPlacement(): { x: number; y: number; } {
        const center = this.getVisibleCanvasCenter();
        return this.findAvailablePosition(center.x, center.y);
    }

    private getDefaultEntityPlacement(): { x: number; y: number; } {
        const center = this.getVisibleCanvasCenter();
        return this.findAvailablePosition(center.x, center.y);
    }

    private getVisibleCanvasCenter(): { x: number; y: number; } {
        const rect = this.container.getBoundingClientRect();
        const clientCenter = {
            x: rect.left + (rect.width / 2),
            y: rect.top + (rect.height / 2),
        };

        return this.paper.clientToLocalPoint(clientCenter);
    }

    private findAvailablePosition(startX: number, startY: number, offsetStep: number = 45): { x: number; y: number; } {
        let x = startX;
        let y = startY;

        const isOccupied = (candidateX: number, candidateY: number) => this.graph.getElements()
            .some(element => {
                if (element instanceof GroupSelection) {
                    return false;
                }

                const position = element.position();
                return position.x === candidateX && position.y === candidateY;
            });

        while (isOccupied(x, y)) {
            x += offsetStep;
            y += offsetStep;
        }

        return { x, y };
    }

    private drawRectangle(evt: joint.dia.Event, x: number, y: number) {
        if (this.startRectangle) {
            this.tempRectangleElement?.remove();

            // Create the rectangle shape (if it is big enough to be visible, based on gridSize)
            if (Math.abs(x - this.startRectangle.x) >= this.paper.options.gridSize
                && Math.abs(y - this.startRectangle.y) >= this.paper.options.gridSize) {
                this.tempRectangleElement = new DrawnRectangle({
                    size: {
                        width: Math.abs(x - this.startRectangle.x),
                        height: Math.abs(y - this.startRectangle.y)
                    },
                    position: {
                        x: this.startRectangle.x > x ? x : this.startRectangle.x,
                        y: this.startRectangle.y > y ? y : this.startRectangle.y
                    },
                });

                // Add the shape to the graph
                this.graph.addCell(this.tempRectangleElement);
            }
        }
    }

    private drawTextBox(evt: joint.dia.Event, x: number, y: number) {
        if (this.startTextBox) {
            this.tempTextBoxElement?.remove();

            if (Math.abs(x - this.startTextBox.x) >= this.paper.options.gridSize
                && Math.abs(y - this.startTextBox.y) >= this.paper.options.gridSize) {

                this.tempTextBoxElement = new TextBox({
                    size: {
                        width: Math.abs(x - this.startTextBox.x),
                        height: Math.abs(y - this.startTextBox.y)
                    },
                    position: {
                        x: this.startTextBox.x > x ? x : this.startTextBox.x,
                        y: this.startTextBox.y > y ? y : this.startTextBox.y
                    },
                });

                this.graph.addCell(this.tempTextBoxElement);
            }
        }
    }

    private drawCallout(evt: joint.dia.Event, x: number, y: number) {
        if (this.startCallout) {
            if (this.tempCalloutElement) {
                this.tempCalloutElement.remove();
            }

            if (Math.abs(x - this.startCallout.x) >= this.paper.options.gridSize
                && Math.abs(y - this.startCallout.y) >= this.paper.options.gridSize) {

                this.tempCalloutElement = new Callout({
                    size: {
                        width: Math.abs(x - this.startCallout.x),
                        height: Math.abs(y - this.startCallout.y)
                    },
                    position: {
                        x: this.startCallout.x > x ? x : this.startCallout.x,
                        y: this.startCallout.y > y ? y : this.startCallout.y
                    },
                });

                this.graph.addCell(this.tempCalloutElement);
            }
        }
    }

    private drawLine(evt: joint.dia.Event, x: number, y: number) {
        if (this.startLine) {
            // Remove the previous temporary line if it exists
            if (this.tempLineElement) {
                this.tempLineElement.remove();
                this.tempLineElement = null;
            }

            // Create the line if it's long enough (at least one grid unit)
            const dx = x - this.startLine.x;
            const dy = y - this.startLine.y;
            const length = Math.sqrt(dx * dx + dy * dy);

            if (length >= (this.paper.options.gridSize as number)) {
                this.tempLineElement = new DrawnLine();
                this.tempLineElement.source({ x: this.startLine.x, y: this.startLine.y });
                this.tempLineElement.target({ x: x, y: y });
                this.tempLineElement.addTo(this.graph);
            }
        }
    }

    private placeIcon(x: number, y: number) {
        if (!this.iconNameToPlace) {
            return;
        }

        const size = 48;
        const icon = new IconElement({
            size: { width: size, height: size },
            position: { x: x, y: y },
            iconName: this.iconNameToPlace,
        });

        icon.setIconName(this.iconNameToPlace);
        this.graph.addCell(icon);

        this.activeTool = null;
        this.iconNameToPlace = null;
        this.dotNetHelper.invokeMethodAsync('SetIconMode', false, null);
    }

    private async addTransaction(source: Node, target: Node, transactionType: TransactionType) {
        // if the source and target are the same, do nothing
        if (source.id != target.id) {

            // Execute transaction creation dialogs regardless of mode
            switch (transactionType) {
                case TransactionType.ServiceFee:
                    await this.addPaymentByDialog(source, target, PaymentType.ServiceFee, FlowType.Service);
                    break;
                case TransactionType.Royalty:
                    await this.addPaymentByDialog(source, target, PaymentType.Royalty, FlowType.IPLicense);
                    break;
                case TransactionType.IPTransfer:
                    await this.addIPTransferByDialog(source, target, FlowType.IPTransfer);
                    break;
                case TransactionType.Loan:
                    await this.addLoanByDialog(source, target);
                    break;
                case TransactionType.Dividend:
                    await this.addDividendByDialog(source, target);
                    break;
                case TransactionType.Capital:
                    await this.addCapitalByDialog(source, target);
                    break;
                case TransactionType.Guarantee:
                    await this.addGuaranteeByDialog(source, target);
                    break;
                default:
                    throw new Error("Unsupported transaction type");
            }
        }
    }

    private getDragPreviewFlowType(mode: DragToolMode): FlowType | null {
        switch (mode) {
            case ToolMode.AddFlow:
                return this.flowTypeToCreate;
            case ToolMode.AddTransaction:
            case ToolMode.MoveRevenueExpense:
                return FlowType.Payment;
            default:
                return null;
        }
    }

    private getActiveDragMode(): DragToolMode | null {
        switch (this.activeTool) {
            case ToolMode.AddFlow:
            case ToolMode.AddTransaction:
            case ToolMode.MoveRevenueExpense:
                return this.activeTool;
            default:
                return null;
        }
    }

    private isValidToolEndpoint(element: joint.dia.Cell | null | undefined, mode: TargetSelectionToolMode, role: FlowEndpointRole): element is FlowEndpoint {
        if (!element) {
            return false;
        }

        switch (mode) {
            case ToolMode.AddFlow:
                return this.isFlowEndpoint(element);
            case ToolMode.AddTransaction:
                return element instanceof Node
                    && !(element instanceof ThirdParty)
                    && (role === 'source' || !this.isGroupOfEntities(element));
            case ToolMode.MoveRevenueExpense:
                return element instanceof Node && !(element instanceof ThirdParty);
            case ToolMode.MoveFunction:
                // AddMoveFunctionStep resolves a single legal entity on each side and no-ops
                // otherwise, so a group (or an entity-less shape) is not a usable target.
                return element instanceof Node
                    && !(element instanceof ThirdParty)
                    && (role === 'source' || element.entityIds?.length === 1);
            default:
                return false;
        }
    }

    private isGroupOfEntities(node: Node): boolean {
        return node instanceof EntityGroup
            || (Array.isArray(node.entityIds) && node.entityIds.length > 1);
    }

    private getDragSourceElement(element: joint.dia.Cell, mode: DragToolMode): FlowEndpoint | null {
        return this.isValidToolEndpoint(element, mode, 'source') ? element : null;
    }

    private getCurrentDragOrMoveFunctionSourceElement(): FlowEndpoint | null {
        if (this.flowDragState?.sourceElement) {
            return this.flowDragState.sourceElement;
        }

        return this.moveFunctionSourceNode;
    }

    private isValidDragTarget(element: joint.dia.Cell | null | undefined, dragState: FlowDragState | null): element is FlowEndpoint {
        return !!dragState
            && element !== dragState.sourceElement
            && this.isValidToolEndpoint(element, dragState.mode, 'target');
    }

    // Move Function's counterpart to isValidDragTarget: the source is chosen from the properties
    // panel rather than by dragging, so it is excluded here instead of via a drag state.
    private isValidMoveFunctionTarget(element: joint.dia.Cell | null | undefined): element is Node {
        return element !== this.moveFunctionSourceNode
            && this.isValidToolEndpoint(element, ToolMode.MoveFunction, 'target');
    }

    private createTemporaryFlowLink(source: joint.dia.Element, flowType: FlowType): joint.shapes.standard.Link {
        const flowInfo = getFlowTypeInfo(flowType);
        const linkClass = (joint.shapes as any)[flowInfo.linkClassName];
        
        if (!linkClass) {
            throw new Error(`Unknown link class: ${flowInfo.linkClassName}`);
        }

        const tempLink = new linkClass();
        applyConnectorStyleMixin(tempLink);
        tempLink.source({ id: source.id, selector: 'body' });
        tempLink.target({ x: 0, y: 0 }); // Will be updated to follow cursor
        tempLink.attr('line/opacity', 0.6); // Semi-transparent to indicate temporary
        tempLink.addTo(this.graph);
        tempLink.toFront();
        
        return tempLink;
    }

    private updateTemporaryFlowLink(tempLink: joint.shapes.standard.Link, point: { x: number; y: number }, target?: FlowEndpoint) {
        if (target) {
            tempLink.target({ id: target.id, selector: 'body' });
            tempLink.attr('line/filter', {
                name: 'dropShadow',
                args: {
                    dx: 0,
                    dy: 0,
                    blur: 4,
                    color: '#0df200',
                }
            }); // Drop shadow when over valid target
            tempLink.attr('line/opacity', 0.8);
        } else {
            tempLink.target(point);
            tempLink.attr('line/filter', null); // No drop shadow when not over valid target
            tempLink.attr('line/opacity', 0.6);
        }
    }

    private addFlow(
        source: joint.dia.Element,
        target: joint.dia.Element,
        flowType: FlowType,
        label?: string | null,
        stepId?: string,
        sourceEntityId?: string,
        targetEntityId?: string,
        notifyLegend = true,
    ) {
        // Two distinct entities can share one node (both members of the same group box). A flow
        // between them is kept but hidden — mirroring the graph-mutation engine — so it survives
        // and is re-shown when a split separates the members again. Without explicit distinct
        // entity ids a same-node link is meaningless (a shape connected to itself), so it is
        // still refused.
        const sameNode = source.id == target.id;
        if (sameNode && !(sourceEntityId && targetEntityId && sourceEntityId !== targetEntityId)) {
            return;
        }

        const newLink: joint.shapes.standard.Link = createFlowLink(flowType);

        // Apply connector style mixin to enable style toggling
        applyConnectorStyleMixin(newLink);

        if (stepId) {
            newLink.prop('stepId', stepId);
        }

        newLink.source(source, { selector: 'body' });
        newLink.target(target, { selector: 'body' });
        const sourceEntityIds = source instanceof Node ? source.entityIds : [];
        const targetEntityIds = target instanceof Node ? target.entityIds : [];

        // Record which entities this flow runs between so group/split/merge operations can
        // reattach it to the right node later. An explicitly passed id wins; otherwise a
        // single-entity endpoint is unambiguous. A multi-entity endpoint without an explicit
        // id stays unattributed (the graph engine's last-resort fallbacks handle those).
        const resolvedSourceEntityId = sourceEntityId ?? (sourceEntityIds.length === 1 ? sourceEntityIds[0] : undefined);
        const resolvedTargetEntityId = targetEntityId ?? (targetEntityIds.length === 1 ? targetEntityIds[0] : undefined);
        if (resolvedSourceEntityId) {
            newLink.prop('sourceEntityId', resolvedSourceEntityId);
        }
        if (resolvedTargetEntityId) {
            newLink.prop('targetEntityId', resolvedTargetEntityId);
        }

        const trimmedLabel = label?.trim();
        if (trimmedLabel) {
            newLink.appendLabel(createFlowLinkTextLabel(trimmedLabel));
        }

        if (sameNode) {
            // Same marker and mechanism as graph-mutations.ts ('root' is joint's implicit
            // selector for the cell view's root element); the engine's same-node visibility
            // pass re-shows the link once its endpoints live on different nodes.
            newLink.prop('hiddenSameNodeLink', true);
            newLink.attr('root/display', 'none');
        }

        if (sourceEntityIds.length > 0 && targetEntityIds.length > 0) {
            if (!stepId && this.futureStateMode === true) {
                // call C# to add AddRelationshipStep if this link is not being added as part of a Future State step creation
                stepId = this.dotNetHelper.invokeMethod('AddRelationshipStep', sourceEntityIds, targetEntityIds, newLink.labels()[0]?.attrs.text.text, flowType);
                newLink.prop('stepId', stepId);
            }
        }

        this.graph.addCell(newLink);

        if (notifyLegend) {
            this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
        }
    }

    // called from C# to change the default connector style for new links
    public setDefaultConnectorStyle(style: string): void {
        setDefaultLinkConnectorStyle(toConnectorStyle(style));
    }

    // Called from C#
    private removeFlowByStepId(stepId: string): void {
        let removed = false;
        expandMergedFlows(this.graph);
        try {
            const linksToRemove = this.graph.getLinks().filter(link =>
                link.prop('stepId') === stepId ||
                ownedStepIds(link).includes(stepId));
            removed = linksToRemove.length > 0;
            linksToRemove.forEach(link => link.remove());
        } finally {
            collapseCoincidentFlows(this.graph);
        }
        if (removed) {
            this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
        }
    }

    // Called from C#
    private toggleFlowElementVisibility(flowType: string, isVisible: boolean) {
        let elements = this.graph.getCells().filter(cell => {
            return cell.attributes.type?.toLowerCase().replace('link', '') === flowType.toLowerCase().replace('link', '');
        });

        elements.forEach(element => {
            // Show/Hide entity cells
            element.attr('body/display', isVisible ? 'block' : 'none');
            const elementView = this.paper.findViewByModel(element);

            if (elementView) {
                const children = elementView.el.querySelectorAll('*');
                children.forEach((child: SVGElement) => {
                    child.setAttribute('display', isVisible ? 'block' : 'none');
                });
            }

            if (element instanceof joint.shapes.standard.Link) {
                element.attr('line/display', isVisible ? 'block' : 'none');
                element.label(0, {
                    attrs: {
                        text: { display: isVisible ? 'block' : 'none' },
                        rect: { display: isVisible ? 'block' : 'none' }
                    }
                });
            }

        });

        this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
    }

    private async openMenu(cell: joint.dia.Cell, x: number, y: number) {
        // 1. Create Menu
        let menu = document.createElement('div');
        menu.className = 'flow-editor-menu';
        // assign a persistent id to the root menu for ancestry tracking
        this.ensureMenuId(menu);

        // 2. Position Menu
        menu.style.top = y + 'px'; //evt.pageY
        menu.style.left = x + 'px'; //evt.pageX

        // 3. Add Menu Options
        let options: MenuOption[] = [];

        if (cell instanceof DrawnRectangle || cell instanceof TextBox || cell instanceof Callout || cell instanceof IconElement) {
            options.push({ text: 'Delete', action: () => { this.closePropertiesPanel(); cell.remove(); } });
        } else if (cell instanceof Node) {
            options.push({ text: 'Delete', action: async () => { await this.confirmDeleteElement(cell) } })

        } else if (cell instanceof joint.shapes.standard.Link) {
            var sourceNode = cell.getSourceElement() as Node;
            var targetNode = cell.getTargetElement() as Node;

            // Build Style submenu options
            const styleSubMenu: MenuOption[] = [
                { 
                    text: 'Sharp', 
                    action: () => { 
                        setGraphConnectorStyle(cell, ConnectorStyle.Sharp);
                        if (this.isLinkSelected(cell)) {
                            this.notifyLinkSelection(cell);
                        }
                    } 
                },
                { 
                    text: 'Rounded', 
                    action: () => { 
                        setGraphConnectorStyle(cell, ConnectorStyle.Rounded);
                        if (this.isLinkSelected(cell)) {
                            this.notifyLinkSelection(cell);
                        }
                    } 
                },
                { 
                    text: 'Curved', 
                    action: () => { 
                        setGraphConnectorStyle(cell, ConnectorStyle.Curved);
                        if (this.isLinkSelected(cell)) {
                            this.notifyLinkSelection(cell);
                        }
                    } 
                },
            ];

            // Build Add Icon Label submenu options
            const iconLabelSubMenu: MenuOption[] = AvailableIconNames.map(iconName => ({
                text: iconName,
                action: async () => { await this.addIconLabelToLink(cell, iconName); }
            }));

            // Build Remove Label submenu options (for links with multiple labels)
            const labels = cell.labels() || [];
            const removeLabelSubMenu: MenuOption[] = labels.map((label: any, index: number) => {
                const labelText = isIconLabel(label) 
                    ? `Icon: ${label.iconName || 'Unknown'}`
                    : label.attrs?.text?.text || `Label ${index + 1}`;
                return {
                    text: labelText,
                    action: () => { this.removeLinkLabel(cell, index); }
                };
            });

            options = [
                { text: 'Add Label', action: () => { this.addTextLabelToLink(cell); } },
                { text: 'Add Icon Label', action: () => {}, subMenu: iconLabelSubMenu },
                ...(labels.length > 1 ? [{ text: 'Remove Label', action: () => {}, subMenu: removeLabelSubMenu }] : []),
                { text: null, action: null },
            ];
            
            // Only show 'Style' options for Flow links, not Lines
            if (!(cell instanceof DrawnLine)) {
                options.push(
                    { text: 'Style', action: () => {}, subMenu: styleSubMenu },
                    { text: null, action: null },
                );
            }
            const deleteText = cell instanceof DrawnLine ? "Delete Line" : "Delete Flow";
            options.push({ text: deleteText, action: () => { this.confirmDeleteRelationship(cell) } })
        }

        options.forEach(option => {
            this.setMenuOptionBehavior(menu, option);
        });
        
        if (options.length == 0) {
            // No options to show - don't display an empty menu
            return;
        }

        // 4. Show Menu
        menu.style.visibility = 'hidden'; // hide the menu to calculate the height
        document.body.appendChild(menu);

        // Calculate the distance between the bottom of the div and the bottom of the window to avoid collisions
        var divBottom = menu.offsetTop + menu.offsetHeight;
        var windowHeight = window.innerHeight || document.documentElement.clientHeight;
        var distanceToBottom = windowHeight - divBottom;

        // Check if the distance will cause the menu to go off the screen
        if (distanceToBottom < 0) {
            menu.style.top = (menu.offsetTop - menu.offsetHeight) + 'px';
        }
        menu.style.visibility = 'visible';
    }
    
    private setMenuOptionBehavior(parent: HTMLDivElement, option: MenuOption) {
        var menuOption = document.createElement('div');

        if (option.text == null && option.action == null) {
            menuOption.className = 'flow-editor-menu-divider';
            parent.appendChild(menuOption);
            return;
        }

        menuOption.className = 'flow-editor-menu-option';
        var menuText = document.createElement('span');
        menuText.textContent = option.text;
        menuOption.appendChild(menuText);

        if (option.subMenu) {
            var arrow = document.createElement('span');
            arrow.textContent = '>';
            menuOption.appendChild(arrow);
        }

        parent.appendChild(menuOption);

        // Prevent mousedown from causing blur on focused elements (e.g., text fields in properties panel).
        // This ensures the click event fires reliably on the menu option.
        menuOption.addEventListener('mousedown', (evt) => {
            evt.preventDefault();
        });

        menuOption.addEventListener('click', async () => {
            // evt.stopPropagation();
            if (option.subMenu) {
                // if there is a sub-menu, open it
                var bounds = menuOption.getBoundingClientRect();
                var x = bounds.left + bounds.width;
                var y = bounds.top;
                this.openSubMenu(x, y, option.subMenu, parent);
            } else {
                // else perform the action
                await option.action();
                this.closeMenus();
            }
        });
    }

    public openSubMenu(x: number, y: number, options: MenuOption[], parentMenu?: HTMLElement) {
        // Make sure the parent has an id so we can preserve its entire ancestor chain
        const parentId = this.ensureMenuId(parentMenu);

        // Close all sub-menus not in the same ancestor chain (preserve all ancestors, not only the immediate parent)
        this.closeSubMenusExcept(parentMenu);

        // 1. Create Sub-Menu with an id to link it to its parent for ancestry tracking
        var menu = document.createElement('div');
        menu.className = 'flow-editor-menu';
        menu.classList.add('flow-editor-submenu');
        const thisId = this.ensureMenuId(menu);
        if (parentId) {
            menu.dataset.parentMenuId = parentId;
        }

        // 2. Position Menu
        menu.style.top = y + 'px';
        menu.style.left = x + 'px';

        // 3. Add Menu Options
        options.forEach(option => {
            this.setMenuOptionBehavior(menu, option);
        });

        // 4. Show Menu
        menu.style.visibility = 'hidden'; // hide the menu to calculate the height
        document.body.appendChild(menu);

        // Calculate the distance between the bottom of the div and the bottom of the window to avoid collisions
        var divBottom = menu.offsetTop + menu.offsetHeight;
        var windowHeight = window.innerHeight || document.documentElement.clientHeight;
        var distanceToBottom = windowHeight - divBottom;

        // Check if the distance will cause the menu to go off the screen
        if (distanceToBottom < 0) {
            menu.style.top = (menu.offsetTop - menu.offsetHeight) + 'px';
        }

        // Calculate the distance between the right of the div and the right of the window to avoid horizontal collisions
        var divRight = menu.offsetLeft + menu.offsetWidth;
        var windowWidth = window.innerWidth || document.documentElement.clientWidth;
        var distanceToRight = windowWidth - divRight;

        // Check if the distance will cause the menu to go off the screen
        if (distanceToRight < 0) {
            menu.style.left = (menu.offsetLeft - menu.offsetWidth) + 'px';
        }

        menu.style.visibility = 'visible';
    }

    public closeMenus() {
        // 1. Retrieve all open menus
        var openMenus = Array.from(document.getElementsByClassName('flow-editor-menu'));

        // 2. Close all open menus
        openMenus.forEach(menu => {
            menu.remove();
        });
    }

    public closeSubMenusExcept(exceptParent?: HTMLElement) {
        // Determine the full ancestor chain for the parent we want to preserve
        const preserveIds = this.collectAncestorIds(exceptParent);

        // retrieve all open sub-menus
        let openSubMenus = Array.from(document.getElementsByClassName('flow-editor-submenu')) as HTMLElement[];

        // close all open sub-menus that are not in the preserved ancestor chain
        openSubMenus.forEach(menu => {
            const id = menu.dataset.menuId;
            if (!id || !preserveIds.has(id)) {
                menu.remove();
            }
        });
    }

    // Helper to resolve names for a set of Entity IDs
    private async getEntityNamesByIds(entityIds: string[]): Promise<Record<string, string>> {
        return await this.dotNetHelper.invokeMethodAsync('GetEntityNamesByIds', entityIds);
    }

    private showDragTargetFeedback(elementView: joint.dia.ElementView, feedback: FlowEndpointFeedback): void {
        if (!this.flowDragState || this.flowDragState.highlightedTarget === elementView.model) {
            return;
        }

        this.clearDragTargetFeedback();
        this.flowDragState.highlightedTarget = elementView.model;
        this.showFlowEndpointFeedback(elementView, feedback, this.flowEndpointHoverHighlighterId);
    }

    private clearDragTargetFeedback(): void {
        if (!this.flowDragState?.highlightedTarget) {
            return;
        }

        this.clearFlowEndpointFeedback(this.flowDragState.highlightedTarget, this.flowEndpointHoverHighlighterId);
        this.flowDragState.highlightedTarget = null;
    }

    private showSourceFeedbackAtPointer(evt: PointerEvent, mode: DragToolMode): void {
        const pointer = this.paper.clientToLocalPoint({ x: evt.clientX, y: evt.clientY });
        const elementView = this.paper.findViewsFromPoint(pointer)
            .find(view => view.model.isElement() && !(view.model instanceof GroupSelection)) as joint.dia.ElementView | undefined;

        if (!elementView) {
            return;
        }

        const isValidSource = this.isValidToolEndpoint(elementView.model, mode, 'source');
        this.showHoveredFlowEndpointFeedback(elementView, isValidSource ? 'valid' : 'invalid');
        this.hoveredElementClickDisabled = !isValidSource;
    }

    private onFlowDragMove(evt: PointerEvent): void {
        if (!this.flowDragState || !this.flowDragState.tempLink) {
            return;
        }

        evt.preventDefault();

        const pointer = this.paper.clientToLocalPoint({ x: evt.clientX, y: evt.clientY });

        const views = this.paper.findViewsFromPoint(pointer);

        const targetView = views.find(view => this.isValidDragTarget(view.model, this.flowDragState)) as joint.dia.ElementView | undefined;
        const targetNode = targetView?.model as FlowEndpoint | undefined;

        if (targetNode && targetView) {
            this.showDragTargetFeedback(targetView, 'valid');
            if (targetNode !== this.flowDragState.currentTarget) {
                this.flowDragState.currentTarget = targetNode;
                this.updateTemporaryFlowLink(this.flowDragState.tempLink, pointer, targetNode);
            }
            return;
        }

        this.flowDragState.currentTarget = null;

        // The source keeps its own 'valid' highlight for the whole drag, and the selection box is
        // not an endpoint; neither may be reported as an invalid target while the pointer is over it.
        const sourceElement = this.flowDragState.sourceElement;
        const invalidTargetView = views.find(view =>
            view.model.isElement()
            && view.model !== sourceElement
            && !(view.model instanceof GroupSelection)
            && !this.isValidDragTarget(view.model, this.flowDragState)) as joint.dia.ElementView | undefined;

        if (invalidTargetView) {
            this.showDragTargetFeedback(invalidTargetView, 'invalid');
        } else {
            this.clearDragTargetFeedback();
        }

        this.updateTemporaryFlowLink(this.flowDragState.tempLink, pointer);
    }

    // Tears down an active flow drag and clears all source/target feedback.
    private cancelFlowDrag(): void {
        if (!this.flowDragState) {
            return;
        }

        const { sourceElement, tempLink } = this.flowDragState;

        this.clearDragTargetFeedback();
        this.clearFlowEndpointFeedback(sourceElement, this.flowDragSourceHighlighterId);

        document.removeEventListener('pointermove', this.flowDragMoveHandler);
        document.removeEventListener('pointerup', this.flowDragEndHandler);
        document.removeEventListener('pointercancel', this.flowDragEndHandler);
        this.flowDragState = null;

        // Remove temporary link
        if (tempLink) {
            tempLink.remove();
        }
    }

    private async onFlowDragEnd(evt: PointerEvent): Promise<void> {
        if (!this.flowDragState) {
            return;
        }

        evt.preventDefault();

        const { sourceElement, currentTarget, mode, dragStartClientPoint } = this.flowDragState;

        if (currentTarget && currentTarget !== sourceElement) {
            this.cancelFlowDrag();

            if (mode === ToolMode.AddFlow && this.flowTypeToCreate !== null) {
                this.addFlow(sourceElement, currentTarget, this.flowTypeToCreate);
                this.showSourceFeedbackAtPointer(evt, mode);
                return;
            }

            if (sourceElement instanceof Node && currentTarget instanceof Node) {
                if (mode === ToolMode.AddTransaction && this.transactionTypeToCreate !== null) {
                    const transactionType = this.transactionTypeToCreate;
                    this.resetToolMode();
                    await this.addTransaction(sourceElement, currentTarget, transactionType);
                    return;
                }

                if (mode === ToolMode.MoveRevenueExpense) {
                    const sourceLabel = (sourceElement.attr('label/text') || '').split('\n')[0];
                    const targetLabel = (currentTarget.attr('label/text') || '').split('\n')[0];
                    this.resetToolMode();
                    await this.dotNetHelper.invokeMethodAsync('AddMoveRevenueExpenseStepByDialog', sourceElement.entityIds, currentTarget.entityIds, sourceLabel, targetLabel);
                    return;
                }
            }
            return;
        }

        // Check if this was a click on the source node (pointerup close to pointerdown)
        const distance = dragStartClientPoint
            ? Math.hypot(evt.clientX - dragStartClientPoint.x, evt.clientY - dragStartClientPoint.y)
            : 0;

        if (distance < 10) {
            // Click-to-connect mode: Keep flowDragState and pointermove active so preview link follows cursor until target is clicked.
            // Remove only pointerup/pointercancel from document for this initial click.
            document.removeEventListener('pointerup', this.flowDragEndHandler);
            document.removeEventListener('pointercancel', this.flowDragEndHandler);
            return;
        }

        this.cancelFlowDrag();

        // The tool remains active after an incomplete drop. Re-evaluate the element under the
        // pointer in the source role because mouseenter will not fire again until it is re-entered.
        this.showSourceFeedbackAtPointer(evt, mode);
    }

    private startFlowDrag(sourceElement: FlowEndpoint, mode: DragToolMode, evt: joint.dia.Event): void {
        const previewFlowType = this.getDragPreviewFlowType(mode);
        if (previewFlowType === null) {
            return;
        }

        evt.stopPropagation();
        evt.preventDefault();

        const clientPoint = this.getClientCoordinatesFromEvent(evt);
        if (!clientPoint) {
            return;
        }

        const pointer = this.paper.clientToLocalPoint(clientPoint);
        const tempLink = this.createTemporaryFlowLink(sourceElement, previewFlowType);
        this.updateTemporaryFlowLink(tempLink, pointer);

        this.flowDragState = {
            sourceElement,
            tempLink,
            currentTarget: null,
            highlightedTarget: null,
            mode,
            dragStartClientPoint: clientPoint,
        };

        joint.highlighters.addClass.removeAll(this.paper, this.flowEndpointHoverHighlighterId);
        joint.highlighters.addClass.removeAll(this.paper, this.flowDragSourceHighlighterId);
        const sourceView = this.paper.findViewByModel(sourceElement) as joint.dia.ElementView | null;
        if (sourceView) {
            this.showFlowEndpointFeedback(sourceView, 'valid', this.flowDragSourceHighlighterId);
        }

        document.addEventListener('pointermove', this.flowDragMoveHandler);
        document.addEventListener('pointerup', this.flowDragEndHandler);
        document.addEventListener('pointercancel', this.flowDragEndHandler);
    }

    // called from C# Properties Panel to begin move function mode
    public beginMoveFunctionFromPanel(elementId: string, entityId: string, functionName: string) {
        // Find the element by ID
        const element = this.graph.getCell(elementId);
        if (element instanceof Node) {
            // clear any existing selection
            this.removeGroupSelection();

            // set the activeTool
            this.activeTool = ToolMode.MoveFunction;
            this.setToolCursorActive(true);

            // set context variables
            this.moveFunctionSourceNode = element;
            this.moveFunctionSourceEntityId = entityId;
            this.functionNameToMove = functionName;

            // visually indicate that this element is selected
            joint.highlighters.addClass.removeAll(this.paper, this.flowDragSourceHighlighterId);
            const elementView = this.paper.findViewByModel(element) as joint.dia.ElementView | null;
            if (elementView) {
                this.showFlowEndpointFeedback(elementView, 'valid', this.flowDragSourceHighlighterId);
            }
            this.closeMenus();
        }
    }

    // called from C#
    private beginAddFlowDirect(flowType: FlowType) {
        // set the activeTool to AddFlow (same as menu-based flow addition)
        this.activeTool = ToolMode.AddFlow;

        // set context variables
        this.flowTypeToCreate = flowType;

        this.setToolCursorActive(true);

        this.closeMenus();

        // move any group selection to the back to allow pointer events to target embedded child elements
        this.groupSelection?.toBack();
    }

    // called from C#
    private beginAddTransactionDirect(transactionType: TransactionType) {
        // set the activeTool to AddTransaction (same as menu-based transaction addition)
        this.activeTool = ToolMode.AddTransaction;

        // set context variables
        this.transactionTypeToCreate = transactionType;

        this.setToolCursorActive(true);

        this.closeMenus();

        // move any group selection to the back to allow pointer events to target embedded child elements
        this.groupSelection?.toBack();
    }

    // called from C#
    private beginMoveRevenueExpenseDirect() {
        // set the activeTool to MoveRevenueExpense
        this.activeTool = ToolMode.MoveRevenueExpense;

        this.setToolCursorActive(true);

        this.closeMenus();

        // move any group selection to the back to allow pointer events to target embedded child elements
        this.groupSelection?.toBack();
    }

    private async addPaymentByDialog(source: Node, target: Node, paymentType: PaymentType, flowType: FlowType): Promise<void> {
        const sourceEntityIds = source.entityIds ?? [];
        const targetEntityIds = target.entityIds ?? [];
        const rawSourceLabel = source.attr('label/text') || '';
        const rawTargetLabel = target.attr('label/text') || '';
        const sourceLabel = rawSourceLabel.split('\n')[0];
        const targetLabel = rawTargetLabel.split('\n')[0];

        const dialogResult = paymentType === PaymentType.ServiceFee
            ? await this.dotNetHelper.invokeMethodAsync('AddServiceFeeByDialog', sourceEntityIds, targetEntityIds, sourceLabel, targetLabel)
            : await this.dotNetHelper.invokeMethodAsync('AddRoyaltyFeeByDialog', sourceEntityIds, targetEntityIds, sourceLabel, targetLabel);

        // Bind new step's ID with the flow/link created
        if (dialogResult && !dialogResult.canceled && !dialogResult.Canceled && dialogResult.data && dialogResult.data[0]) {
            const stepId = dialogResult.data[0].id || dialogResult.data[0].Id;

            // Add the Service or Royalty flow
            this.addFlow(target, source, flowType, null, stepId);
            // Add the Payment flow
            this.addFlow(source, target, FlowType.Payment, null, stepId);
        }
    }

    private async addIPTransferByDialog(transferee: Node, transferor: Node, flowType: FlowType): Promise<void> {
        const sourceEntityIds = transferee.entityIds ?? [];
        const targetEntityIds = transferor.entityIds ?? [];
        const rawSourceLabel = transferee.attr('label/text') || '';
        const rawTargetLabel = transferor.attr('label/text') || '';
        const sourceLabel = rawSourceLabel.split('\n')[0];
        const targetLabel = rawTargetLabel.split('\n')[0];

        const dialogResult = await this.dotNetHelper.invokeMethodAsync('AddIpTransferStepByDialog', sourceEntityIds, targetEntityIds, sourceLabel, targetLabel);

        // Bind new step's ID with the flow/link created
        if (dialogResult && !dialogResult.canceled && !dialogResult.Canceled && dialogResult.data && dialogResult.data[0]) {
            const stepId = dialogResult.data[0].id || dialogResult.data[0].Id;

            // Add the IP Transfer flow
            this.addFlow(transferor, transferee, flowType, null, stepId);
            // Add the Payment flow
            this.addFlow(transferee, transferor, FlowType.Payment, null, stepId);
        }
    }

    private async addPnlByDialog(node: Node) {
        this.closeMenus();
        const entityId = node.entityIds[0]; // there should only be one entity id (for a new entity)
        await this.dotNetHelper.invokeMethodAsync('EditPnlByDialog', entityId);
    }

    // called from C#
    public async addEntityNode() {
        const entityIdsToExclude: string[] = [];
        this.graph.getElements().forEach(node => {
            if (node instanceof Node) {
                entityIdsToExclude.push(...node.entityIds);
            }
        });

        const results: EntityNodeOptions[] = await this.dotNetHelper.invokeMethodAsync('SearchEntitiesByDialog', entityIdsToExclude);
        if (results != null) {
            let nextPosition = this.getDefaultEntityPlacement();
            const offsetStep = 45;

            results.forEach(result => {
                // create the new type of element based on the incoming entity(s)
                const newElement: Node = this.CreateEntityNode(result.shape);

                // set the entityIds of the new element
                newElement.entityIds = result.entityIds;
                newElement.isNewEntity = result.isNewEntity;

                // Apply Org Chart matching styling & label
                this.applyOrgChartStylingToNode(newElement, result);

                newElement.position(nextPosition.x, nextPosition.y);
                this.graph.addCell(newElement);

                nextPosition = this.findAvailablePosition(nextPosition.x + offsetStep, nextPosition.y + offsetStep, offsetStep);
            });

            await this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
        }
    }

    private async addLoanByDialog(borrower: Node, lender: Node): Promise<void> {
        this.closeMenus();

        // Borrower pays interest to lender (a payment flow from borrower -> lender)
        const sourceEntityIds = borrower.entityIds ?? [];
        const targetEntityIds = lender.entityIds ?? [];
        const rawSourceLabel = borrower.attr('label/text') || '';
        const rawTargetLabel = lender.attr('label/text') || '';
        const sourceLabel = rawSourceLabel.split('\n')[0];
        const targetLabel = rawTargetLabel.split('\n')[0];

        const dialogResult = await this.dotNetHelper.invokeMethodAsync('AddLoanByDialog', sourceEntityIds, targetEntityIds, sourceLabel, targetLabel);

        if (dialogResult && !dialogResult.canceled && !dialogResult.Canceled && dialogResult.data && dialogResult.data[0]) {
            const stepId = dialogResult.data[0].id || dialogResult.data[0].Id;
            // Draw Payment flow from borrower to lender and also a Legal/Service link (TBD from Deloitte.) For now, just Payment.
            this.addFlow(borrower, lender, FlowType.Payment, undefined, stepId);
        }
    }

    private async addDividendByDialog(source: Node, target: Node): Promise<void> {
        this.closeMenus();

        const sourceEntityIds = source.entityIds ?? [];
        const targetEntityIds = target.entityIds ?? [];
        const rawSourceLabel = source.attr('label/text') || '';
        const rawTargetLabel = target.attr('label/text') || '';
        const sourceLabel = rawSourceLabel.split('\n')[0];
        const targetLabel = rawTargetLabel.split('\n')[0];

        const dialogResult = await this.dotNetHelper.invokeMethodAsync('AddDividendByDialog', sourceEntityIds, targetEntityIds, sourceLabel, targetLabel);

        if (dialogResult && !dialogResult.canceled && !dialogResult.Canceled && dialogResult.data && dialogResult.data[0]) {
            const stepId = dialogResult.data[0].id || dialogResult.data[0].Id;
            this.addFlow(source, target, FlowType.Payment, "Dividend", stepId);
        }
    }

    private async addCapitalByDialog(source: Node, target: Node): Promise<void> {
        this.closeMenus();

        const sourceEntityIds = source.entityIds ?? [];
        const targetEntityIds = target.entityIds ?? [];
        const rawSourceLabel = source.attr('label/text') || '';
        const rawTargetLabel = target.attr('label/text') || '';
        const sourceLabel = rawSourceLabel.split('\n')[0];
        const targetLabel = rawTargetLabel.split('\n')[0];

        const dialogResult = await this.dotNetHelper.invokeMethodAsync('AddCapitalByDialog', sourceEntityIds, targetEntityIds, sourceLabel, targetLabel);

        if (dialogResult && !dialogResult.canceled && !dialogResult.Canceled && dialogResult.data && dialogResult.data[0]) {
            const stepId = dialogResult.data[0].id || dialogResult.data[0].Id;
            this.addFlow(source, target, FlowType.Payment, "Capital", stepId);
        }
    }

    private async addGuaranteeByDialog(source: Node, target: Node): Promise<void> {
        this.closeMenus();

        const sourceEntityIds = source.entityIds ?? [];
        const targetEntityIds = target.entityIds ?? [];
        const rawSourceLabel = source.attr('label/text') || '';
        const rawTargetLabel = target.attr('label/text') || '';
        const sourceLabel = rawSourceLabel.split('\n')[0];
        const targetLabel = rawTargetLabel.split('\n')[0];

        const dialogResult = await this.dotNetHelper.invokeMethodAsync('AddGuaranteeByDialog', sourceEntityIds, targetEntityIds, sourceLabel, targetLabel);

        if (dialogResult && !dialogResult.canceled && !dialogResult.Canceled && dialogResult.data && dialogResult.data[0]) {
            const stepId = dialogResult.data[0].id || dialogResult.data[0].Id;
            this.addFlow(source, target, FlowType.Payment, "Guarantee", stepId);
        }
    }

    // called from C#
    public async addThirdPartyNode() {
        const entityIdsToExclude: string[] = [];
        this.graph.getElements().forEach(node => {
            if (node instanceof Node) {
                entityIdsToExclude.push(...node.entityIds);
            }
        });

        const results: EntityNodeOptions[] = await this.dotNetHelper.invokeMethodAsync('SearchExternalEntitiesByDialog', entityIdsToExclude);
        if (results != null) {
            let nextPosition = this.getDefaultEntityPlacement();
            const offsetStep = 45;

            results.forEach(result => {
                // create the new type of element based on the incoming entity(s)
                const newElement: Node = this.CreateEntityNode(result.shape);
                // set the entityIds of the new element
                newElement.entityIds = result.entityIds;
                newElement.isNewEntity = result.isNewEntity;

                // Apply Org Chart matching styling & label
                this.applyOrgChartStylingToNode(newElement, result);

                newElement.position(nextPosition.x, nextPosition.y);
                this.graph.addCell(newElement);

                nextPosition = this.findAvailablePosition(nextPosition.x + offsetStep, nextPosition.y + offsetStep, offsetStep);
            });
            await this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
        }
    }

    // called from C# — adds a visual-only 3rd Party shape with no entity linkage
    public async addUnlinkedThirdPartyNode() {
        const newElement: Node = this.CreateEntityNode(ShapeType.ThirdParty);
        newElement.entityIds = [];
        this.applyOrgChartStylingToNode(newElement, {
            jurisdiction: Jurisdiction.ThirdParty,
            shape: ShapeType.ThirdParty,
            label: 'Third Party',
            entityIds: [],
            isNewEntity: false,
            diagramType: 'External',
        });

        const position = this.getDefaultEntityPlacement();
        newElement.position(position.x, position.y);
        this.graph.addCell(newElement);

        await this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
    }

    //// TODO: allow changing the entity for a node that was already added.
    //// This should call C# to update any Future State steps that are associated with the existing entity.
    //private async chooseEntities(element: joint.dia.Element) {
    //    // first get all entity ids from all nodes in the graph to exclude from search
    //    var entityIdsToExclude: string[] = [];
    //    this.graph.getElements().forEach(node => {
    //        if (node instanceof Node) {
    //            entityIdsToExclude.push(...node.entityIds);
    //        }
    //    });

    //    var results: EntityNodeOptions[];

    //    if (element instanceof EmptyThirdParty || element instanceof BranchThirdParty) {
    //        results = await this.dotNetHelper.invokeMethodAsync('SearchExternalEntitiesByDialog', entityIdsToExclude);
    //    }
    //    else {
    //        results = await this.dotNetHelper.invokeMethodAsync('SearchEntitiesByDialog', entityIdsToExclude);
    //    }
    //    //console.log('result: ' + result);

    //    if (results != null) {
    //        // do something
    //    }
    //}

    private resolveDiagramType(options: EntityNodeOptions): string {
        return resolveDiagramType(options);
    }

    private measureLabelText(text: string): number {
        return measureLabelText(text);
    }

    private getFlagImageUrl(countryCode?: string): string | null {
        return getFlagImageUrl(countryCode);
    }

    private applyOrgChartStylingToNode(node: Node, options: EntityNodeOptions): void {
        applyOrgChartStylingToNode(node, options);
    }


    // called from C# to add a new entity node to the graph
    public async addEntityByEntityNodeOptions(options: EntityNodeOptions, x?: number, y?: number) {
        // check if any of the element ids already exist in the graph
        const element = this.graph.getElements().find(e => e instanceof Node &&
            e.entityIds.filter(id => options.entityIds.includes(id)).length > 0);

        if (element) {
            console.error('error: cannot add duplicate entity id(s) to graph');
            return;
        }

        const newElement: Node = this.CreateEntityNode(options.shape);

        // set the entityIds of the new element
        newElement.entityIds = options.entityIds;

        // Apply Org Chart matching styling & label
        this.applyOrgChartStylingToNode(newElement, options);

        // set the isNewEntity property of the new element
        newElement.isNewEntity = options.isNewEntity;

        const defaultPos = this.getDefaultEntityPlacement();
        const posX = typeof x === 'number' ? x : defaultPos.x;
        const posY = typeof y === 'number' ? y : defaultPos.y;

        newElement.position(posX, posY);

        this.graph.addCell(newElement);
        await this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
    }

    private onCanvasDragOver(evt: DragEvent) {
        const types = evt.dataTransfer?.types;
        if (types && (types.includes('application/x-vca-entity') || types.includes('text/plain'))) {
            evt.preventDefault();
            if (evt.dataTransfer) {
                evt.dataTransfer.dropEffect = 'copy';
            }
        }
    }

    private onCanvasDragEnter(evt: DragEvent) {
        const types = evt.dataTransfer?.types;
        if (types && (types.includes('application/x-vca-entity') || types.includes('text/plain'))) {
            this.container.classList.add('flow-drop-target-active');
        }
    }

    private onCanvasDragLeave(evt: DragEvent) {
        if (evt.relatedTarget === null || (evt.relatedTarget instanceof HTMLElement && !this.container.contains(evt.relatedTarget))) {
            this.container.classList.remove('flow-drop-target-active');
        }
    }

    private async onCanvasDrop(evt: DragEvent) {
        this.container.classList.remove('flow-drop-target-active');
        const entityId = evt.dataTransfer?.getData('application/x-vca-entity') || evt.dataTransfer?.getData('text/plain');
        if (!entityId) return;

        evt.preventDefault();

        // Check if entity already exists on graph
        const existing = this.graph.getElements().find(e => e instanceof Node && e.entityIds && e.entityIds.includes(entityId));
        if (existing) {
            await this.dotNetHelper.invokeMethodAsync('OnEntityAlreadyPlaced', entityId);
            return;
        }

        const localPoint = this.paper.clientToLocalPoint({ x: evt.clientX, y: evt.clientY });
        const nodeX = Math.round(localPoint.x - 112);
        const nodeY = Math.round(localPoint.y - 41);

        await this.dotNetHelper.invokeMethodAsync('AddEntityToSandboxAtPosition', entityId, nodeX, nodeY);
    }

    // called from C# to replace an entity node with a new one
    public async replaceEntityNodeByEntityId(entityId: string, options: EntityNodeOptions) {
        var element = this.graph.getElements().find(e => e instanceof Node &&
            e.entityIds.length == 1 && e.entityIds[0] == entityId);

        if (element) {
            const newElement: Node = this.CreateEntityNode(options.shape);

            // set the entityIds of the new element
            newElement.entityIds = options.entityIds;
            newElement.isNewEntity = options.isNewEntity;

            // Apply Org Chart matching styling & label
            this.applyOrgChartStylingToNode(newElement, options);

            // set the position of the new element to the same position as the old element
            newElement.position(element.attributes.position.x, element.attributes.position.y);
            this.graph.addCell(newElement);

            // update any existing links to reference the new element
            var links = this.graph.getConnectedLinks(element);
            links.forEach(link => {
                if (link.get('source').id === element.id) {
                    var source = link.source();
                    // replace the source id with the new element id
                    source.id = newElement.id;
                    link.source(source);
                }
                if (link.get('target').id === element.id) {
                    var target = link.target();
                    // replace the target id with the new element id
                    target.id = newElement.id;
                    link.target(target);
                }
            });

            // remove the original element
            element.remove();
            await this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
        }
    }

    private async confirmDeleteElement(element: joint.dia.Element) {
        this.hideEntityActionBar();
        this.closeMenus();
        await this.dotNetHelper.invokeMethodAsync('ClosePropertiesPanel');
        
        if (this.futureStateMode && element instanceof Node) {
            
            var entityIds = element.entityIds ?? [];
            var label = element.attr('label/text');
            var result = await this.dotNetHelper.invokeMethodAsync('ConfirmDeleteEntityByDialog', entityIds, label);
            if (result) {
                element.remove();
            }
        }
        else {
            element.remove();
        }
        await this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
    }

    private async confirmDeleteRelationship(link: joint.shapes.standard.Link) {
        this.closeMenus();
        await this.dotNetHelper.invokeMethodAsync('ClosePropertiesPanel');
        
        // In Future State, we need to confirm deletion of relationships (i.e., links that are not DrawnLines)
        if (this.futureStateMode && !(link instanceof DrawnLine)) {
            const stepIds = ownedStepIds(link);
            const flowCount = 1 + getMergedFlows(link).length;

            const result = await this.dotNetHelper.invokeMethodAsync(
                'ConfirmDeleteRelationshipByDialog',
                stepIds,
                flowCount,
            );
            if (result) {
                // Unlike adding a link, deletion does not create a RemoveRelationshipStep because
                // there is no way to visualize one.
                link.remove();
                await this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
            }
        }
        else {
            link.remove();
            await this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
        }
    }

    // called from C# to remove an entity node from the graph
    public async removeEntityNodeByEntityId(entityId: string) {
        var element = this.graph.getElements().find(e => e instanceof Node &&
            e.entityIds.length == 1 && e.entityIds[0] == entityId);

        if (element) {
            element.remove();
            await this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
        }
    }

    private onEscapeKey(evt: KeyboardEvent) {
        if (evt.key === 'Escape') {
            this.closeMenus();
            this.resetToolMode();
            this.removeGroupSelection();
            this.clearLinkSelection();
            this.removeTools();
            this.closePropertiesPanel();
        }
    }

    private getKeyboardEventElement(evt: KeyboardEvent): Element | null {
        if (evt.target instanceof Element) {
            return evt.target;
        }

        return document.activeElement;
    }

    private isTypingContext(evt: KeyboardEvent): boolean {
        const target = this.getKeyboardEventElement(evt);

        if (!target) {
            return false;
        }

        if (target instanceof HTMLElement && target.isContentEditable) {
            return true;
        }

        return target.closest('input, textarea, select, [contenteditable], [role="textbox"]') !== null;
    }

    private onDeleteKey(evt: KeyboardEvent) {
        if (evt.key !== 'Delete') {
            return;
        }

        if (this.isTypingContext(evt)) {
            return;
        }

        this.deleteSelectedItems();
    }

    public zoomIn() {
        this.zoomAroundCentre(this.paper.scale().sx + CanvasZoom.step);
    }

    public zoomOut() {
        this.zoomAroundCentre(this.paper.scale().sx - CanvasZoom.step);
    }

    /** Back to the opening view: the flow centred at 100%. */
    public resetZoom() {
        this.closeMenus();
        this.paper.scale(1, 1);
        this.paper.transformToFitContent({ verticalAlign: 'middle', horizontalAlign: 'middle', minScale: 1, maxScale: 1 });
    }

    /** Rescales around the canvas centre, so the point under it stays put. */
    private zoomAroundCentre(nextScale: number) {
        const scale = this.paper.scale().sx;
        const next = Math.max(CanvasZoom.min, Math.min(CanvasZoom.max, nextScale));
        const { tx, ty } = this.paper.translate();
        const centreX = this.container.clientWidth / 2;
        const centreY = this.container.clientHeight / 2;
        const worldX = (centreX - tx) / scale;
        const worldY = (centreY - ty) / scale;

        this.closeMenus();
        this.paper.scale(next, next);
        this.paper.translate(centreX - worldX * next, centreY - worldY * next);
    }

    public resetToolMode() {
        // cancel any active flow drag operation and clear transient endpoint feedback
        this.cancelFlowDrag();
        this.clearAllFlowEndpointFeedback();

        this.activeTool = null;
        this.iconNameToPlace = null;
        this.panStartPosition = null;

        this.setToolCursorActive(false);

        this.moveFunctionSourceNode = null;
        this.moveFunctionSourceEntityId = null;
        this.functionNameToMove = null;

        this.flowTypeToCreate = null;
        this.transactionTypeToCreate = null;

        this.startRectangle = null;
        this.tempRectangleElement?.remove();

        this.startCallout = null;
        if (this.tempCalloutElement) {
            this.tempCalloutElement.remove();
            this.tempCalloutElement = null;
        }

        this.startLine = null;
        if (this.tempLineElement) {
            this.tempLineElement.remove();
            this.tempLineElement = null;
        }

        this.cancelCalloutTailDrag();

        // Clear the active flow button highlight in Blazor UI
        this.dotNetHelper.invokeMethodAsync('ClearActiveFlowType');
        // Clear the active transaction button highlight in Blazor UI
        if (this.futureStateMode) {
            this.dotNetHelper.invokeMethodAsync('ClearActiveTransactionType');
        }
        
        // move any group selection to the front because no active tool needs to target its embedded children
        this.groupSelection?.toFront();
    }

    private async changeElementPrimaryText(element: Node | Callout | TextBox) {
        // get the current text of the entity
        var oldText = element.attr('label/text');

        var newText = await this.dotNetHelper.invokeMethodAsync('EditTextByDialog', oldText);

        // if newText is not null (allow empty strings)
        if (newText != null) {
            element.setLabel(newText);
        }
    }

    private async changeElementSecondaryText(element: Node) {
        // get the current text of the entity
        var oldText = element.attr('label2/text');

        var newText = await this.dotNetHelper.invokeMethodAsync('EditTextByDialog', oldText);

        // if newText is not null (allow empty strings)
        if (newText != null) {
            element.setSecondaryLabel(newText);
        }
    }

    private notifyElementSelection(element: joint.dia.Element) {
        const elementId = element.id.toString();
        const elementType = element.get('type') || 'unknown';
        
        let primaryLabel: string | null = null;
        let secondaryLabel: string | null = null;
        let backgroundColor: string | null = null;
        let textColor: string | null = null;
        let borderColor: string | null = null;
        let shapeColor: string | null = null;
        let entityIds: string[] = [];
        let isNewEntity = false;
        let iconName: string | null = null;

        // The registry owns support; these branches only read concrete values.
        const kind = classifyCell(element);
        const hasPrimaryLabelSupport = toCapabilityId(kind, 'primaryLabel') !== null;
        const hasSecondaryLabelSupport = toCapabilityId(kind, 'secondaryLabel') !== null;
        const hasBackgroundColorSupport = toCapabilityId(kind, 'backgroundColor') !== null;
        const hasTextColorSupport = toCapabilityId(kind, 'textColor') !== null;
        const hasBorderColorSupport = toCapabilityId(kind, 'borderColor') !== null;
        const hasShapeColorSupport = toCapabilityId(kind, 'shapeColor') !== null;
        const hasIconNameSupport = toCapabilityId(kind, 'iconName') !== null;

        // Extract properties based on element type
        if (element instanceof EntityGroup) {
            primaryLabel = element.attr('label/text') || '';
            secondaryLabel = element.attr('label2/text') || '';
            backgroundColor = element.attr('body1/fill') || element.attr('body2/fill') || '';
            textColor = element.attr('label/fill') || '';
            entityIds = element.entityIds || [];
            isNewEntity = element.isNewEntity || false;
        } else if (element instanceof Node) {
            primaryLabel = element.attr('label/text') || '';
            secondaryLabel = element.attr('label2/text') || '';
            backgroundColor = element.attr('body/fill') || '';
            textColor = element.attr('label/fill') || '';
            entityIds = element.entityIds || [];
            isNewEntity = element.isNewEntity || false;
        } else if (element instanceof TextBox) {
            primaryLabel = element.attr('label/text') || '';
            backgroundColor = element.attr('body/fill') || '';
            textColor = element.attr('label/fill') || '';
            borderColor = element.attr('body/stroke') || '';
        } else if (element instanceof Callout) {
            primaryLabel = element.attr('label/text') || '';
            backgroundColor = element.attr('body/fill') || '';
            textColor = element.attr('label/fill') || '';
        } else if (element instanceof IconElement) {
            iconName = element.getIconName();
            shapeColor = element.getShapeColor();
        } else if (element instanceof DrawnRectangle) {
            borderColor = element.attr('body/stroke') || '';
        }

        if (element instanceof Node) {
            this.showEntityActionBar(element);
        } else {
            this.hideEntityActionBar();
        }

        // Notify C# about the element selection
        this.dotNetHelper.invokeMethodAsync('OnElementSelected', 
            elementId, elementType, primaryLabel, secondaryLabel, backgroundColor, textColor, borderColor, shapeColor,
            entityIds, hasPrimaryLabelSupport, hasSecondaryLabelSupport, hasShapeColorSupport, isNewEntity,
            iconName, hasBackgroundColorSupport, hasTextColorSupport, hasBorderColorSupport,
            hasIconNameSupport, hasIconNameSupport ? AvailableIconNames : []);
    }

    private showEntityActionBar(node: Node) {
        // The bar's Details/Adjust buttons call back into the host (OnEntityAction). Only hosts that
        // implement it enable the bar; elsewhere the buttons would reject on click.
        if (!this.showEntityActions) {
            this.hideEntityActionBar();
            return;
        }

        this.selectedEntityNode = node;
        if (!this.entityActionBarEl) {
            this.createEntityActionBar();
        }
        this.updateEntityActionBarPosition();
        if (this.entityActionBarEl) {
            this.entityActionBarEl.style.display = 'inline-flex';
        }
    }

    private hideEntityActionBar() {
        this.selectedEntityNode = null;
        if (this.entityActionBarEl) {
            this.entityActionBarEl.style.display = 'none';
        }
    }

    private createEntityActionBar() {
        if (this.entityActionBarEl) return;

        const bar = document.createElement('div');
        bar.className = 'flow-editor-entity-action-bar';
        bar.setAttribute('role', 'toolbar');
        bar.setAttribute('aria-label', 'Entity actions');
        
        // Prevent canvas panning, dragging, or deselecting when interacting with toolbar
        bar.addEventListener('pointerdown', (e) => e.stopPropagation());
        bar.addEventListener('mousedown', (e) => e.stopPropagation());
        bar.addEventListener('click', (e) => e.stopPropagation());

        // 1. Details (Info)
        const btnDetails = this.createActionButton(
            'Details',
            '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg>',
            () => {
                const entityId = this.getEntityIdForSelectedNode();
                if (entityId) {
                    this.dotNetHelper.invokeMethodAsync('OnEntityAction', entityId, 'details');
                }
            }
        );

        // 2. Direct Adjustments
        const btnAdjust = this.createActionButton(
            'Adjust — Direct adjustments',
            '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" y1="4" x2="14" y2="4"></line><line x1="10" y1="4" x2="3" y2="4"></line><line x1="21" y1="12" x2="12" y2="12"></line><line x1="8" y1="12" x2="3" y2="12"></line><line x1="21" y1="20" x2="16" y2="20"></line><line x1="12" y1="20" x2="3" y2="20"></line><line x1="14" y1="2" x2="14" y2="6"></line><line x1="8" y1="10" x2="8" y2="14"></line><line x1="16" y1="18" x2="16" y2="22"></line></svg>',
            () => {
                const entityId = this.getEntityIdForSelectedNode();
                if (entityId) {
                    this.dotNetHelper.invokeMethodAsync('OnEntityAction', entityId, 'directAdjustments');
                }
            }
        );

        // 3. Delete
        const btnDelete = this.createActionButton(
            'Remove from canvas',
            '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>',
            async () => {
                const node = this.selectedEntityNode;
                if (node) {
                    this.hideEntityActionBar();
                    await this.confirmDeleteElement(node);
                }
            },
            true
        );

        bar.appendChild(btnDetails);
        bar.appendChild(btnAdjust);
        bar.appendChild(btnDelete);

        this.container.appendChild(bar);
        this.entityActionBarEl = bar;
    }

    private createActionButton(title: string, svgHtml: string, onClick: () => void, isDanger = false): HTMLButtonElement {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'flow-editor-action-btn' + (isDanger ? ' flow-editor-action-btn-danger' : '');
        btn.title = title;
        btn.setAttribute('aria-label', title);
        btn.innerHTML = svgHtml;
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            onClick();
        });
        return btn;
    }

    private getEntityIdForSelectedNode(): string | null {
        if (!this.selectedEntityNode) return null;
        const entityIds = this.selectedEntityNode.entityIds;
        if (entityIds && entityIds.length > 0) {
            return entityIds[0];
        }
        return this.selectedEntityNode.id?.toString() ?? null;
    }

    private updateEntityActionBarPosition() {
        if (!this.entityActionBarEl || !this.selectedEntityNode) return;
        const bbox = this.selectedEntityNode.getBBox();
        const origin = this.paper.localToClientPoint(bbox.x, bbox.y);
        const containerRect = this.container.getBoundingClientRect();
        const scale = this.paper.scale().sx;
        const nodeLeft = origin.x - containerRect.left;
        const nodeTop = origin.y - containerRect.top;
        const nodeWidth = bbox.width * scale;
        const nodeHeight = bbox.height * scale;
        const centerX = nodeLeft + nodeWidth / 2;
        const clearance = 44;
        const gap = 8;
        const below = nodeTop < clearance;

        this.entityActionBarEl.style.left = `${centerX}px`;
        this.entityActionBarEl.style.top = below ? `${nodeTop + nodeHeight + gap}px` : `${nodeTop - gap}px`;
        this.entityActionBarEl.style.transform = below ? 'translateX(-50%)' : 'translate(-50%, -100%)';
    }

    private notifyLinkSelection(link: joint.shapes.standard.Link) {
        if (link instanceof DrawnLine) {
            return;
        }
        const linkId = link.id.toString();
        const linkType = link.get('type') || 'standard.Link';
        
        // Get all labels with their details (common for both DrawnLine and other links)
        const labels = link.labels() || [];
        const labelInfos = labels.map((label: any, index: number) => {
            const isIcon = isIconLabel(label);
            return {
                index: index,
                isIconLabel: isIcon,
                text: isIcon ? '' : (label.attrs?.text?.text || ''),
                iconName: isIcon ? (label.iconName || '') : '',
            };
        });
        
        // Get primary label text (first text label) for backward compatibility
        const textLabels = labelInfos.filter(l => !l.isIconLabel);
        const primaryLabelText = textLabels.length > 0 ? textLabels[0].text : '';
        
        // Get connector style - read from link's property, falling back to mixin method or default
        let connectorStyle: ConnectorStyle;
        if (isConnectorStyleable(link)) {
            connectorStyle = link.getConnectorStyle();
        } else {
            // Even without the mixin, the link may have the style stored as a property
            connectorStyle = (link.prop('connectorStyle') as ConnectorStyle) || getDefaultLinkConnectorStyle();
        }
        
        // Get link color
        const linkColor = link.attr('line/stroke') as string || '#000000';
        
        // Notify C# about the link selection with full label details
        this.dotNetHelper.invokeMethodAsync('OnLinkSelected', 
            linkId, linkType, primaryLabelText, connectorStyle, linkColor, labelInfos);
    }

    private applyLinkSelectionVisuals(link: joint.shapes.standard.Link): void {
        const linkView = link.findView(this.paper);
        if (!linkView) {
            return;
        }
        joint.highlighters.mask.add(linkView, 'root', 'selected-link-highlighter', {
            deep: true,
            padding: 7,
            attrs: {
                'stroke': '#00a6ff',
                'stroke-width': 2,
            }
        });
        linkView.addTools(this.buildLinkTools(link));
        link.toFront();
    }

    private clearLinkSelectionVisuals(link: joint.shapes.standard.Link): void {
        const linkView = link.findView(this.paper);
        if (!linkView) {
            return;
        }
        joint.highlighters.mask.remove(linkView, 'selected-link-highlighter');
        linkView.removeTools();
    }


    private buildLinkTools(link: joint.shapes.standard.Link): joint.dia.ToolsView {
        const tools = [];
        if (link instanceof DrawnLine) {
            const sourceTool = new DrawnLineSourceTool();
            const targetTool = new DrawnLineTargetTool();
            tools.push(sourceTool, targetTool);
        } else {
            const verticesTool = new joint.linkTools.Vertices({
                snapRadius: 30,
                stopPropagation: false,
            });
            tools.push(verticesTool);
            const connectorStyle = isConnectorStyleable(link)
                ? link.getConnectorStyle()
                : (link.prop('connectorStyle') as ConnectorStyle) || getDefaultLinkConnectorStyle();
            if (connectorStyle !== ConnectorStyle.Curved) {
                const segmentsTool = new joint.linkTools.Segments({
                    snapRadius: 15,
                    stopPropagation: false,
                });
                tools.push(segmentsTool);
            }
        }
        return new joint.dia.ToolsView({ tools });
    }

    private selectLink(link: joint.shapes.standard.Link, options?: { preserveGroupSelection?: boolean }) {
        // Deselect any previous link selection
        this.clearLinkSelection();
        if (!options?.preserveGroupSelection) {
            this.removeGroupSelection();
        }
        
        this.selectedLink = link;

        this.applyLinkSelectionVisuals(link);
        
        // Notify C# about the selection
        this.notifyLinkSelection(link);
    }

    private clearLinkSelection() {
        if (this.selectedLink) {
            this.clearLinkSelectionVisuals(this.selectedLink);
            this.selectedLink = null;
        }
    }

    private isLinkSelected(link: joint.shapes.standard.Link): boolean {
        return this.selectedLink?.id === link.id;
    }

    private notifyGroupSelection() {
        const children = this.getGroupSelectionChildren();
        if (children.length <= 1) {
            // If 0 or 1 items, this isn't really a group selection
            if (children.length === 1) {
                const single = children[0];
                if (single instanceof joint.dia.Element) {
                    this.notifyElementSelection(single);
                }
            } else {
                this.closePropertiesPanel();
            }
            if (children.length === 0 || !(children[0] instanceof DrawnLine)) {
                return;
            }
        }

        // Count items by type
        let nodeCount = 0;
        let textBoxCount = 0;
        let calloutCount = 0;
        let rectangleCount = 0;
        let iconElementCount = 0;
        let drawnLineCount = 0;
        
        // The registry owns support and maps DrawnLine borderColor to strokeColor.
        const groupSupports = (uiProperty: string) =>
            children.every(child => toCapabilityId(classifyCell(child), uiProperty) !== null);
        const allSupportFillColor = groupSupports('backgroundColor');
        const allSupportTextColor = groupSupports('textColor');
        const allSupportBorderColor = groupSupports('borderColor');
        const allSupportShapeColor = groupSupports('shapeColor');

        // Track common values (null means not yet set, undefined means values differ)
        let commonFillColor: string | null | undefined = null;
        let commonTextColor: string | null | undefined = null;
        let commonBorderColor: string | null | undefined = null;
        let commonShapeColor: string | null | undefined = null;

        let lineId: string | null = null;
        let lineStrokeWidth: number | null = null;
        let lineLabelInfos: Array<{ index: number; isIconLabel: boolean; text: string; iconName: string }> | null = null;

        const entityNodeInfos: Array<{ elementId: string; elementType: string; entityIds: string[] }> = [];

        for (const child of children) {
            if (child instanceof Node) {
                nodeCount++;
                if (child.entityIds?.length) {
                    entityNodeInfos.push({
                        elementId: child.id.toString(),
                        elementType: child.get('type') || 'unknown',
                        entityIds: [...child.entityIds],
                    });
                }
                // Check background color
                const bgColor = child.attr('body/fill') as string;
                if (commonFillColor === null) {
                    commonFillColor = bgColor;
                } else if (commonFillColor !== bgColor) {
                    commonFillColor = undefined;
                }
                
                // Check text color
                const txtColor = child.attr('label/fill') as string;
                if (commonTextColor === null) {
                    commonTextColor = txtColor;
                } else if (commonTextColor !== txtColor) {
                    commonTextColor = undefined;
                }
            } else if (child instanceof TextBox) {
                textBoxCount++;

                // Check fill color
                const bgColor = child.attr('body/fill') as string;
                if (commonFillColor === null) {
                    commonFillColor = bgColor;
                } else if (commonFillColor !== bgColor) {
                    commonFillColor = undefined;
                }
                
                // Check text color
                const txtColor = child.attr('label/fill') as string;
                if (commonTextColor === null) {
                    commonTextColor = txtColor;
                } else if (commonTextColor !== txtColor) {
                    commonTextColor = undefined;
                }
                
                // Check border color
                const borderColor = child.attr('body/stroke') as string;
                if (commonBorderColor === null) {
                    commonBorderColor = borderColor;
                } else if (commonBorderColor !== borderColor) {
                    commonBorderColor = undefined;
                }
            } else if (child instanceof Callout) {
                calloutCount++;

                // Check fill color
                const bgColor = child.attr('body/fill') as string;
                if (commonFillColor === null) {
                    commonFillColor = bgColor;
                } else if (commonFillColor !== bgColor) {
                    commonFillColor = undefined;
                }
                
                // Check text color
                const txtColor = child.attr('label/fill') as string;
                if (commonTextColor === null) {
                    commonTextColor = txtColor;
                } else if (commonTextColor !== txtColor) {
                    commonTextColor = undefined;
                }
            } else if (child instanceof DrawnRectangle) {
                rectangleCount++;

                // Check border color
                const borderColor = child.attr('body/stroke') as string;
                if (commonBorderColor === null) {
                    commonBorderColor = borderColor;
                } else if (commonBorderColor !== borderColor) {
                    commonBorderColor = undefined;
                }
            } else if (child instanceof IconElement) {
                iconElementCount++;

                // Check shape color
                const shapeColor = child.getShapeColor();
                if (commonShapeColor === null) {
                    commonShapeColor = shapeColor;
                } else if (commonShapeColor !== shapeColor) {
                    commonShapeColor = undefined;
                }
            } else if (child instanceof DrawnLine) {
                drawnLineCount++;

                // Check stroke color (treated as border color)
                const strokeColor = child.getStrokeColor();
                if (commonBorderColor === null) {
                    commonBorderColor = strokeColor;
                } else if (commonBorderColor !== strokeColor) {
                    commonBorderColor = undefined;
                }

                if (children.length === 1) {
                    lineId = child.id.toString();
                    lineStrokeWidth = child.getStrokeWidth();
                    const labels = child.labels() || [];
                    lineLabelInfos = labels.map((label: any, index: number) => {
                        const isIcon = isIconLabel(label);
                        return {
                            index,
                            isIconLabel: isIcon,
                            text: isIcon ? '' : (label.attrs?.text?.text || ''),
                            iconName: isIcon ? (label.iconName || '') : '',
                        };
                    });
                }
            }
        }

        // Notify C# about the group selection
        this.dotNetHelper.invokeMethodAsync('OnGroupSelected',
            children.length, nodeCount, textBoxCount, calloutCount, rectangleCount, iconElementCount, drawnLineCount,
            allSupportFillColor, allSupportTextColor, allSupportBorderColor, allSupportShapeColor,
            commonFillColor ?? null, commonTextColor ?? null, commonBorderColor ?? null, commonShapeColor ?? null,
            lineId, lineStrokeWidth, lineLabelInfos, entityNodeInfos);
    }

    // Update a specific property for all items in the group selection (called by C#)
    public async updateGroupSelectionProperty(propertyName: string, value: string) {
        const children = this.getGroupSelectionChildren();

        for (const child of children) {
            // The registry maps panel aliases; type guards only narrow the setter call.
            const capabilityId = toCapabilityId(classifyCell(child), propertyName);
            if (capabilityId === null) continue;

            await setGraphElementProperty(child, capabilityId, value);
        }
    }

    private closePropertiesPanel() {
        this.hideEntityActionBar();
        this.dotNetHelper.invokeMethodAsync('ClosePropertiesPanel');
    }

    // Delete the currently selected item(s) from the canvas (called by C# properties panel)
    public async deleteSelectedItems() {
        // 1. Single or multiple selected item(s) will be children of a group selection. 
        const groupChildren = this.getGroupSelectionChildren();
        if (groupChildren.length > 0) {
            // Collect children first, then tear down the group before removing cells
            const cellsToDelete = [...groupChildren];
            this.removeGroupSelection();
            
            // For multiple items, show a consolidated confirmation
            if (cellsToDelete.length > 1) {
                const confirmed = await this.dotNetHelper.invokeMethodAsync('ConfirmDeleteMultipleItemsByDialog', cellsToDelete.length);
                if (!confirmed) {
                    return;
                }
                
                // Delete all items without individual confirmations
                await this.dotNetHelper.invokeMethodAsync('ClosePropertiesPanel');
                
                for (const cell of cellsToDelete) {
                    // For Future State, we need to delete associated steps for all nodes and for links that are not DrawnLines.
                    if (this.futureStateMode) {
                        if (cell instanceof Node) {
                            const entityIds = cell.entityIds ?? [];
                            for (const entityId of entityIds) {
                                await this.dotNetHelper.invokeMethodAsync('DeleteStepsForEntityId', entityId);
                            }
                        } else if (cell instanceof joint.shapes.standard.Link && !(cell instanceof DrawnLine)) {
                            for (const stepId of ownedStepIds(cell)) {
                                await this.dotNetHelper.invokeMethodAsync('RemoveRelationshipStepById', stepId);
                            }
                        }
                    }
                    cell.remove();
                }
            } else if (cellsToDelete.length === 1) {
                // Single item: use individual confirmations
                const cell = cellsToDelete[0];
                if (cell instanceof Node) {
                    await this.confirmDeleteElement(cell);
                } else if (cell instanceof joint.shapes.standard.Link && !(cell instanceof DrawnLine)) {
                    await this.confirmDeleteRelationship(cell);
                } else {
                    cell.remove();
                }
            }
            
            if (this.futureStateMode) {
                await this.dotNetHelper.invokeMethodAsync('UpdateWhatIf');
            }
            await this.dotNetHelper.invokeMethodAsync('SetFlowLegendItems');
            return;
        }

        // 2. If no group selection, delete any selected link (flow arrow) (note only one can be selected at a time)
        if (this.selectedLink) {
            const link = this.selectedLink;
            this.clearLinkSelection();
            await this.confirmDeleteRelationship(link);
            return;
        }
    }

    // The methods below are the Blazor properties-panel adapters: each delegates to the shared
    // graph actions in graph-actions.ts. They return silently on transient input and stale
    // selections — a half-typed stroke width, a label index from a selection that has since
    // changed — because a keystroke must not throw across interop. The graph actions re-check
    // the same conditions and throw, because the declarative mutation path needs hard errors.
    // Keep both layers: deduping the guards here turns a stray keystroke into a JSException,
    // and dropping the throws there lets a bad agent patch through silently.
    
    // Update element properties (called by C#)
    public async updateElementProperty(elementId: string, propertyName: string, value: string) {
        const element = this.graph.getCell(elementId);
        if (!element) return;

        // The registry maps panel aliases; type guards only narrow the setter call.
        const capabilityId = toCapabilityId(classifyCell(element), propertyName);
        if (capabilityId === null) return;

        await setGraphElementProperty(element, capabilityId, value);
    }

    // Update link properties (called by C#)
    public updateLinkProperty(linkId: string, propertyName: string, value: string) {
        const link = this.graph.getCell(linkId) as joint.shapes.standard.Link | undefined;
        if (!link || !(link instanceof joint.shapes.standard.Link)) return;

        switch (propertyName) {
            case 'primaryLabel':
                // Update the first text label
                const labels = link.labels() || [];
                const textLabelIndex = labels.findIndex((label: any) => !isIconLabel(label));
                if (textLabelIndex >= 0) {
                    updateGraphLinkTextLabel(link, textLabelIndex, value);
                }
                break;
            case 'connectorStyle':
                setGraphConnectorStyle(link, value);
                break;
        }
    }

    // Update line properties (called by C#)
    public async updateLineProperty(lineId: string, propertyName: string, value: string) {
        const line = this.graph.getCell(lineId) as DrawnLine | undefined;
        if (!line || !(line instanceof DrawnLine)) return;

        if (propertyName === 'strokeWidth') {
            const width = parseFloat(value);
            if (!Number.isFinite(width) || width <= 0) {
                return;
            }
            await setGraphElementProperty(line, propertyName, width);
            if (this.groupSelection && this.isCellInGroupSelection(line)) {
                this.updateGroupSelectionBounds();
            }
            return;
        }

        await setGraphElementProperty(line, propertyName, value);
    }

    // Update a specific label's text by index (called by C#)
    public updateLinkLabelText(linkId: string, labelIndex: number, newText: string) {
        const link = this.graph.getCell(linkId) as joint.shapes.standard.Link | undefined;
        if (!link || !(link instanceof joint.shapes.standard.Link)) return;
        
        const labels = link.labels() || [];
        if (labelIndex < 0 || labelIndex >= labels.length) return;
        
        // Only update text labels (not icon labels)
        if (isIconLabel(labels[labelIndex])) return;
        
        updateGraphLinkTextLabel(link, labelIndex, newText);
        
        // Refresh the selection notification
        if (link instanceof DrawnLine) {
            this.notifyGroupSelection();
            return;
        }
        if (this.isLinkSelected(link)) {
            this.notifyLinkSelection(link);
        }
    }

    // Remove a label by index (called by C#)
    public removeLinkLabelById(linkId: string, labelIndex: number) {
        const link = this.graph.getCell(linkId) as joint.shapes.standard.Link | undefined;
        if (!link || !(link instanceof joint.shapes.standard.Link)) return;
        
        const labels = link.labels() || [];
        if (labelIndex < 0 || labelIndex >= labels.length) return;
        
        removeGraphLinkLabel(link, labelIndex);
        
        // Refresh the selection notification
        if (link instanceof DrawnLine) {
            this.notifyGroupSelection();
            return;
        }
        if (this.isLinkSelected(link)) {
            this.notifyLinkSelection(link);
        }
    }

    // Add a new text label to the selected link (called by C#)
    public async addLinkTextLabel(linkId: string) {
        const link = this.graph.getCell(linkId) as joint.shapes.standard.Link | undefined;
        if (!link || !(link instanceof joint.shapes.standard.Link)) return;
        
        const newText = await this.dotNetHelper.invokeMethodAsync('EditTextByDialog', '');
        
        if (newText != null && newText.length > 0) {
            addGraphLinkTextLabel(link, newText);
            
            // Refresh the selection notification
            if (link instanceof DrawnLine) {
                this.notifyGroupSelection();
            } else if (this.isLinkSelected(link)) {
                this.notifyLinkSelection(link);
            }
        }
    }

    // Add an icon label to the selected link (called by C#)
    public async addLinkIconLabel(linkId: string, iconName: string) {
        const link = this.graph.getCell(linkId) as joint.shapes.standard.Link | undefined;
        if (!link || !(link instanceof joint.shapes.standard.Link)) return;
        
        try {
            await addGraphLinkIconLabel(link, iconName);
            
            // Refresh the selection notification
            if (link instanceof DrawnLine) {
                this.notifyGroupSelection();
            } else if (this.isLinkSelected(link)) {
                this.notifyLinkSelection(link);
            }
        } catch (error) {
            console.error('Failed to add icon label to link:', error);
        }
    }

    // Used internally to add a text label via the link context menu
    private async addTextLabelToLink(link: joint.shapes.standard.Link) {
        const newText = await this.dotNetHelper.invokeMethodAsync('EditTextByDialog', '');
        
        if (newText != null && newText.length > 0) {
            addGraphLinkTextLabel(link, newText);
        }
    }

    // Used internally to add an icon label via the link context menu
    private async addIconLabelToLink(link: joint.shapes.standard.Link, iconName: string) {
        try {
            await addGraphLinkIconLabel(link, iconName);
        } catch (error) {
            console.error('Failed to add icon label to link:', error);
        }
    }

    // Used internally to remove a label via the link context menu
    private removeLinkLabel(link: joint.shapes.standard.Link, labelIndex: number) {
        const labels = link.labels() || [];
        if (labelIndex >= 0 && labelIndex < labels.length) {
            removeGraphLinkLabel(link, labelIndex);
        }
    }

    private async changeElementColor(element: IFillColorable) {
        const oldColor = element.getColor();

        var color = await this.dotNetHelper.invokeMethodAsync('ChooseColorByDialog', oldColor);

        // if color is not null or empty string
        if (color) {
            element.setColor(color);
        }
    }

    private async changeElementFontColor(element: ITextColorable) {
        const oldColor = element.getFontColor();
        
        var color = await this.dotNetHelper.invokeMethodAsync('ChooseColorByDialog', oldColor);
        // if color is not null or empty string
        if (color) {
            element.setFontColor(color);
        }
    }

    private CreateEntityNode(shapeType: ShapeType): Node {
        return createEntityNode(shapeType);
    }

    constructor(options: FlowEditorOptions) {
        this.graph = new joint.dia.Graph({}, { cellNamespace: this.namespace });

        registerGraphGenerationTracking(this.graph, () => {
            this.graphGeneration += 1;
        });

        this.graph.on('remove', (cell: joint.dia.Cell) => {
            if (cell === this.groupSelection) {
                // unembed all children cells
                this.getGroupSelectionChildren().forEach(child => this.groupSelection.unembed(child));
                this.groupSelection = null;
                return;
            } else if (this.groupSelection && this.getGroupSelectionChildren().length == 0) {
                // remove the group selection if it has no more children
                this.groupSelection.remove();
                this.groupSelection = null;
            }
        });

        this.graph.on('change:source change:target', (cell: joint.dia.Cell) => {
            if (!(cell instanceof DrawnLine)) {
                return;
            }

            if (this.isCellInGroupSelection(cell)) {
                this.updateGroupSelectionBounds();
            }
        });

        // When the user manually drags an element, drop its agent-added marker so future
        // AI auto-layout passes treat it as user-owned and leave its position untouched.
        // JointJS tags user-initiated moves with opt.ui (and propagates it to embedded
        // children of a group drag); programmatic moves do not set it.
        this.graph.on('change:position', (cell: joint.dia.Element, _position: joint.dia.Point, opt: { ui?: boolean }) => {
            if (opt?.ui && cell.prop('agentAdded') === true) {
                cell.prop('agentAdded', false);
            }
        });

        this.paper = new joint.dia.Paper({
            el: options.container,
            model: this.graph,
            width: null, // this is controlled by CSS
            height: null, // this is controlled by CSS
            gridSize: 15,
            drawGrid: {
                name: 'dot', args: { color: '#000000' }
            },
            interactive: (cellView, eventName) => {
                const baseOptions = { linkMove: true, labelMove: true } as any;

                if (toolModesWithTargetSelection.includes(this.activeTool)) {
                    // Source-target tools own pointer interaction; prevent JointJS element dragging.
                    return { ...baseOptions, elementMove: false };
                }

                // Disable element movement during flow drag
                if (this.flowDragState) {
                    return { ...baseOptions, elementMove: false };
                }

                if (cellView?.model instanceof Callout && this.calloutTailDragState) {
                    // when dragging the callout tail, disable all other interactions
                    return { ...baseOptions, elementMove: false };
                }

                return { ...baseOptions, elementMove: true };
            },
            snapLabels: true,
            labelsLayer: true,
            clickThreshold: 2,
            moveThreshold: 2,
            cellViewNamespace: this.namespace
        });

        this.dotNetHelper = options.dotNetHelper;
        this.futureStateMode = options.futureStateMode;
        this.showEntityActions = options.showEntityActions === true;
        this.container = options.container;

        this.paper.on('translate scale', () => {
            this.updateEntityActionBarPosition();
        });

        this.graph.on('change:position change:size', (cell: joint.dia.Cell) => {
            if (this.selectedEntityNode && cell === this.selectedEntityNode) {
                this.updateEntityActionBarPosition();
            }
        });

        this.graph.on('remove', (cell: joint.dia.Cell) => {
            if (this.selectedEntityNode && cell === this.selectedEntityNode) {
                this.hideEntityActionBar();
            }
        });

        this.paper.on('blank:mouseover', () => {
            // check for ongoing resize state before removing tools
            if (this.resizeState) {
                return;
            }
            
            // check for single entity in a group selection before removing tools
            if (this.groupSelection && this.getGroupSelectionChildren().length == 1) {
                const child = this.getGroupSelectionChildren()[0];
                if (child instanceof DrawnLine) {
                    // ensure link tools are shown for the single DrawnLine
                    const linkView = child.findView(this.paper);
                    if (linkView && !linkView.hasTools()) {
                        // clear any temporary tools that may have been added on hover to a different element
                        this.removeTools();
                        linkView.addTools(this.buildLinkTools(child));
                    }
                } else if (child instanceof joint.dia.Element) {
                    // ensure the tools are shown for the single child element
                    const childView = this.paper.findViewByModel(child) as joint.dia.ElementView | null;
                    if (childView) {
                        const toolsView = this.createToolsViewForElement(child as joint.dia.Element);
                        if (toolsView) {
                            // clear any temporary tools that may have been added on hover to a different element
                            this.removeTools();
                            if (child instanceof Callout) {
                                child.showTailHandle();
                            }
                            const childView = this.paper.findViewByModel(child) as joint.dia.ElementView | null;
                            childView?.addTools(toolsView);
                        }
                    }
                }
                return;
            }
            
            // check for open menus before removing tools
            var openMenus = Array.from(document.getElementsByClassName('flow-editor-menu'));
            if (openMenus.length == 0) {
                this.removeTools();
            }
            
            if (this.selectedLink != null) {
                // re-add link tools if a link is selected
                const linkView = this.selectedLink.findView(this.paper);
                if (linkView && !linkView.hasTools()) {
                    linkView.addTools(this.buildLinkTools(this.selectedLink));
                }
            }
        });

        this.paper.on({
            'link:mouseenter': (linkView, evt) => {
                evt.stopPropagation();
                // check for open menus before adding tools on hover for DrawnLine only (other Link types only show tools when selected)
                var openMenus = Array.from(document.getElementsByClassName('flow-editor-menu'));
                if (openMenus.length > 0) {
                    return;
                }
                else if (linkView.model instanceof DrawnLine) {
                    const toolsView = this.buildLinkTools(linkView.model);
                    if (toolsView) {
                        this.removeTools();
                        linkView.addTools(toolsView);
                    }
                }
            },
            'element:mouseenter': (cellView, evt) => {
                if (this.resizeState) {
                    // prevent interference with ongoing resize
                    return;
                }
                
                let element = cellView.model;
                const dragMode = this.getActiveDragMode();

                if (element instanceof GroupSelection) {
                    const openMenus = Array.from(document.getElementsByClassName('flow-editor-menu'));
                    if (openMenus.length === 0) {
                        const toolsView = this.createGroupSelectionTools();
                        if (toolsView) {
                            this.removeTools();
                            cellView.addTools(toolsView);
                        }
                    }
                    return;
                }

                if (dragMode !== null) {
                    // Pointer-move owns target feedback once a drag has started. Before that,
                    // mouseenter evaluates the element in the source role.
                    if (this.flowDragState) {
                        return;
                    }

                    const isValidSource = this.isValidToolEndpoint(element, dragMode, 'source');
                    this.showHoveredFlowEndpointFeedback(cellView, isValidSource ? 'valid' : 'invalid');
                    this.hoveredElementClickDisabled = !isValidSource;
                    return;
                }

                if (this.activeTool === ToolMode.MoveFunction) {
                    const isValidTarget = this.isValidMoveFunctionTarget(element);
                    this.showHoveredFlowEndpointFeedback(cellView, isValidTarget ? 'valid' : 'invalid');
                    this.hoveredElementClickDisabled = !isValidTarget;
                    return;
                }
                    
                // check for open menus before adding tools on hover
                var openMenus = Array.from(document.getElementsByClassName('flow-editor-menu'));
                if (openMenus.length == 0) {
                    const toolsView = this.createToolsViewForElement(cellView.model as joint.dia.Element);
                    if (toolsView) {
                        this.removeTools();
                        if (element instanceof Callout) {
                            element.showTailHandle();
                        }
                        cellView.addTools(toolsView);
                    }
                }
            },
            'element:mouseleave': (cellView, evt) => {
                let element = cellView.model;
                const dragMode = this.getActiveDragMode();

                if ((dragMode !== null && !this.flowDragState) || this.activeTool === ToolMode.MoveFunction) {
                    joint.highlighters.addClass.remove(cellView, this.flowEndpointHoverHighlighterId);
                    this.hoveredElementClickDisabled = false;
                }

                // Hide callout tail handle when leaving the Callout (unless dragging or only child of a group selection)
                if (!this.calloutTailDragState && element instanceof Callout && !this.isOnlyChildOfGroupSelection(element)) {
                    element.hideTailHandle();
                }
            },
            'element:pointerup': (cellView, evt) => {
                // Re-enable tail-tip absolute lock (if it was suppressed during a drag)
                if (cellView.model instanceof Callout) {
                    cellView.model.prop('suppressTailTipLock', false);
                }
            },
            'element:contextmenu': (cellView, evt) => {
                evt.stopPropagation();
                this.closeMenus();
                const element = cellView.model;
                this.openMenu(element, evt.pageX, evt.pageY);
            },
            'element:pointerclick': async (cellView, evt, x, y) => {
                if (this.hoveredElementClickDisabled) {
                    evt.stopPropagation();
                    return;
                }

                this.closeMenus();
                const element = cellView.model;
                
                // Clicking a group-selectable cell should clear any non-group link selection
                if (this.isGroupSelectableCell(element)) {
                    this.clearLinkSelection();
                }
                
                // Flow and transaction/move-revenue tools are drag-only and are finalized in onFlowDragEnd.
                if (this.getActiveDragMode() !== null) {
                    return;
                }

                if (this.activeTool === ToolMode.MoveFunction) {
                    // The tool owns the click while it is active; an invalid target is a no-op
                    // rather than falling through to selection.
                    evt.stopPropagation();
                    if (this.isValidMoveFunctionTarget(element)
                        && this.moveFunctionSourceNode && this.moveFunctionSourceEntityId && this.functionNameToMove) {
                        this.dotNetHelper.invokeMethodAsync('AddMoveFunctionStep', [this.moveFunctionSourceEntityId], element.entityIds, this.functionNameToMove);
                        this.resetToolMode();
                    }
                    return;
                }

                // Handle selection (no active tool)
                if (this.activeTool != null) return;
                
                if (element instanceof GroupSelection) {
                    this.clearLinkSelection();
                    // Click on GroupSelection - check for cell behind the group selection to handle the click
                    const cellsBehind = this.graph.findCellsAtPoint(new joint.g.Point(x, y));
                    if (cellsBehind?.length > 0) {
                        evt.stopPropagation();
                        this.handleGroupSelectableClick(cellsBehind[0], evt.shiftKey);
                    }
                } else if (this.isGroupSelectableCell(element)) {
                    evt.stopPropagation();
                    this.handleGroupSelectableClick(element, evt.shiftKey);
                }
            },
            'link:pointerdown': (cellView, evt) => {
                this.closeMenus();
                const link = cellView.model as joint.shapes.standard.Link;
                if (this.isNonGroupSelectableLink(link)) {
                    this.removeGroupSelection();
                }
            },
            'link:pointerclick': (cellView, evt) => {
                evt.stopPropagation();
                const link = cellView.model as joint.shapes.standard.Link;
                
                if (link instanceof DrawnLine) {
                    this.handleGroupSelectableClick(link, evt.shiftKey);
                    return;
                }

                // Non-group-selectable link
                this.selectLink(link);
            },
            'link:contextmenu': (cellView, evt) => {
                evt.stopPropagation();
                this.closeMenus();
                const link = cellView.model as joint.shapes.standard.Link;
                this.openMenu(link, evt.pageX, evt.pageY);
            },
            'callout:tail:pointerdown': (cellView, evt) => {
                evt.stopPropagation();
                evt.preventDefault();
                const model = cellView.model;
                if (model instanceof Callout) {
                    this.beginCalloutTailDrag(evt, model);
                }
            },
            'element:pointerdown': async (cellView, evt) => {
                const element = cellView.model;
                const dragMode = this.getActiveDragMode();

                if (dragMode != null) {
                    // Source-target tools always consume pointerdown to avoid accidental element drag.
                    evt.preventDefault();
                    evt.stopPropagation();

                    // If a source is already selected (e.g. click-to-connect mode) and user clicked an element:
                    if (this.flowDragState && this.flowDragState.sourceElement) {
                        if (this.isValidDragTarget(element, this.flowDragState)) {
                            const sourceElement = this.flowDragState.sourceElement;
                            const targetElement = element;
                            const mode = this.flowDragState.mode;
                            this.cancelFlowDrag();

                            if (mode === ToolMode.AddFlow && this.flowTypeToCreate !== null) {
                                this.addFlow(sourceElement, targetElement, this.flowTypeToCreate);
                                return;
                            }

                            if (sourceElement instanceof Node && targetElement instanceof Node) {
                                if (mode === ToolMode.AddTransaction && this.transactionTypeToCreate !== null) {
                                    const transactionType = this.transactionTypeToCreate;
                                    this.resetToolMode();
                                    await this.addTransaction(sourceElement, targetElement, transactionType);
                                    return;
                                }

                                if (mode === ToolMode.MoveRevenueExpense) {
                                    const sourceLabel = (sourceElement.attr('label/text') || '').split('\n')[0];
                                    const targetLabel = (targetElement.attr('label/text') || '').split('\n')[0];
                                    this.resetToolMode();
                                    await this.dotNetHelper.invokeMethodAsync('AddMoveRevenueExpenseStepByDialog', sourceElement.entityIds, targetElement.entityIds, sourceLabel, targetLabel);
                                    return;
                                }
                            }
                            return;
                        } else if (element === this.flowDragState.sourceElement) {
                            // Clicking the same source element again cancels the pending connection
                            this.cancelFlowDrag();
                            return;
                        }
                    }

                    const dragSource = this.getDragSourceElement(element, dragMode);
                    if (dragSource) {
                        this.startFlowDrag(dragSource, dragMode, evt);
                    }
                    return;
                }
                
                // Handle callout dragging
                if (element instanceof Callout) {
                    const target = evt.target as EventTarget | null;
                    if (!this.isCalloutTailHandle(target) && !this.isResizeHandle(target)) {
                        // Begin normal drag of callout; mark suppression so onPositionChanged doesn't lock tip to absolute
                        element.prop('suppressTailTipLock', true);
                    }
                }
            },
            'element:pointermove': (cellView, evt, x, y) => {
                if (this.calloutTailDragState) {
                    return;
                }
                
                // Don't reset tool mode during flow drag
                if (this.flowDragState) {
                    return;
                }

                if (this.shiftSelectionStart) {
                    const coords = this.getClientCoordinatesFromEvent(evt as unknown as joint.dia.Event);
                    if (coords) {
                        const paperPoint = this.paper.clientToLocalPoint(coords);
                        this.updateShiftGroupSelection(paperPoint.x, paperPoint.y);
                    }
                    return;
                }

                const model = cellView.model;
                const isDrawingTemporaryElement =
                    (this.activeTool == ToolMode.DrawRectangle && model === this.tempRectangleElement)
                    || (this.activeTool == ToolMode.DrawTextBox && model === this.tempTextBoxElement)
                    || (this.activeTool == ToolMode.DrawCallout && model === this.tempCalloutElement);

                if (isDrawingTemporaryElement) {
                    return;
                }

                if (this.activeTool == ToolMode.DrawIcon) {
                    return;
                }

                // Source-target tool modes should remain active until drag end/cancel.
                if (toolModesWithTargetSelection.includes(this.activeTool)) {
                    return;
                }

                this.closeMenus();
                this.resetToolMode();
                this.updateGroupSelectionBounds();
            },
            'blank:pointerdown': (evt, x, y) => {
                this.closeMenus();
                
                // Deselect any selected link
                this.clearLinkSelection();

                // check for single entity in a group selection before removing tools
                if (!this.groupSelection || this.getGroupSelectionChildren().length !== 1) {
                    this.removeTools();
                }
                
                if (this.activeTool == ToolMode.DrawRectangle) {
                    this.startRectangle = { x: x, y: y };
                }
                else if (this.activeTool == ToolMode.DrawTextBox) {
                    this.startTextBox = { x: x, y: y };
                }
                else if (this.activeTool == ToolMode.DrawCallout) {
                    this.startCallout = { x: x, y: y };
                }
                else if (this.activeTool == ToolMode.DrawLine) {
                    this.startLine = { x: x, y: y };
                }
                else if (this.activeTool !== null) {
                    // Do not pan while any tool is active
                    if (this.flowDragState) {
                        this.cancelFlowDrag();
                    }
                }
                else {
                    const pointerEvent = evt as unknown as PointerEvent;
                    if (pointerEvent?.shiftKey) {
                        this.beginShiftGroupSelection(x, y);
                    } else {
                        // Logic to start panning the paper
                        const scale = this.paper.scale();
                        this.panStartPosition = { x: x * scale.sx, y: y * scale.sy };
                    }
                }
            },
            'blank:pointerup': async (evt, x, y) => {
                if (this.activeTool == ToolMode.DrawRectangle && this.startRectangle) {
                    this.startRectangle = null;
                    this.tempRectangleElement = null;
                    await this.dotNetHelper.invokeMethodAsync('SetRectangleMode', false);
                } else if (this.activeTool == ToolMode.DrawTextBox && this.startTextBox) {
                    this.startTextBox = null;
                    this.tempTextBoxElement = null;
                    await this.dotNetHelper.invokeMethodAsync('SetTextBoxMode', false);
                } else if (this.activeTool == ToolMode.DrawCallout && this.startCallout) {
                    this.startCallout = null;
                    this.tempCalloutElement = null;
                    await this.dotNetHelper.invokeMethodAsync('SetCalloutMode', false);
                } else if (this.activeTool == ToolMode.DrawLine && this.startLine) {
                    this.startLine = null;
                    this.tempLineElement = null;
                    await this.dotNetHelper.invokeMethodAsync('SetLineMode', false);
                } else if (this.shiftSelectionStart) {
                    this.completeShiftGroupSelection({ x, y });
                } else {
                    // Logic to stop panning the paper
                    this.panStartPosition = null;
                }
            },
            'blank:pointermove': (evt, x, y) => {
                if (this.activeTool == ToolMode.DrawRectangle) {
                    this.drawRectangle(evt, x, y);
                } else if (this.activeTool == ToolMode.DrawTextBox) {
                    this.drawTextBox(evt, x, y);
                } else if (this.activeTool == ToolMode.DrawCallout) {
                    this.drawCallout(evt, x, y);
                } else if (this.activeTool == ToolMode.DrawLine) {
                    this.drawLine(evt, x, y);
                } else if (this.shiftSelectionStart) {
                    this.updateShiftGroupSelection(x, y);
                }
            },
            'blank:pointerclick': (evt, x, y) => {
                if (this.activeTool == ToolMode.DrawIcon) {
                    this.placeIcon(x, y);
                }
                
                if (this.flowDragState) {
                    this.cancelFlowDrag();
                }

                this.resetToolMode();
                this.removeGroupSelection();
                this.removeTools();
                this.closePropertiesPanel();
            }
        });

        // Panning and Zooming
        // General approach from: https://medium.com/@ishaanshettigar/guide-panning-and-zooming-in-jointjs-for-free-step-by-step-tutorial-4a56590d2761

        // handle panning the paper
        // https://stackoverflow.com/questions/28431384/how-to-make-a-paper-draggable
        this.container.addEventListener('mousemove', (evt) => {
            if (this.panStartPosition && this.activeTool === null) {
                this.paper.translate(
                    evt.offsetX - this.panStartPosition.x,
                    evt.offsetY - this.panStartPosition.y);
            }
        });

        // handle zooming the paper
        // https://stackoverflow.com/a/69196310
        this.container.addEventListener('wheel', evt => {
            evt.preventDefault();
            let scale = this.paper.scale().sx;

            // Get the current translation
            let translate = this.paper.translate();

            // Get mouse position relative to the document
            let mouseX = evt.pageX;
            let mouseY = evt.pageY;

            // Convert to position relative to the paper
            let x = (mouseX - this.container.offsetLeft - translate.tx) / scale;
            let y = (mouseY - this.container.offsetTop - translate.ty) / scale;

            const offsetX = Math.trunc(evt.clientX - this.container.offsetLeft);
            const offsetY = Math.trunc(evt.clientY - this.container.offsetTop);

            // Normalize the deltaY
            let delta = evt.deltaY;
            switch (evt.deltaMode) {
                case 1: // Delta in lines
                    delta *= 14; // Approximate line height in pixels
                    break;
                case 2: // Delta in pages
                    delta *= window.innerHeight; // Full page height
                    break;
                // case 0: default is pixels, no conversion needed
            }
            
            // Adjust this multiplier for zoom sensitivity
            const zoomSpeed = .001;
            const oldScale = this.paper.scale().sx;
            const newScale = oldScale - delta * zoomSpeed;

            if (newScale > CanvasZoom.min && newScale < CanvasZoom.max) {
                this.closeMenus();
                this.paper.scale(newScale, newScale);
                this.paper.translate(-x * newScale + offsetX, -y * newScale + offsetY);
            }
        });

        // handle resize of the window
        window.addEventListener('resize', () => {
            var currentScale = this.paper.scale();
            this.paper.transformToFitContent({ verticalAlign: 'middle', horizontalAlign: 'middle', minScale: currentScale.sx, maxScale: currentScale.sx });
        });

        // Handle Escape key to deselect elements and close properties panel
        document.addEventListener('keydown', this.escapeKeyHandler);
        // Handle Delete key to delete selected items
        document.addEventListener('keydown', this.deleteKeyHandler);

        // Handle drag and drop of entities from Entities tab onto canvas
        this.container.addEventListener('dragover', this.canvasDragOverHandler);
        this.container.addEventListener('dragenter', this.canvasDragEnterHandler);
        this.container.addEventListener('dragleave', this.canvasDragLeaveHandler);
        this.container.addEventListener('drop', this.canvasDropHandler);
    }

    public dispose() {
        this.closeMenus();
        this.removeGroupSelection();
        this.cancelFlowDrag();
        this.clearAllFlowEndpointFeedback();
        this.setToolCursorActive(false);
        document.removeEventListener('keydown', this.escapeKeyHandler);
        document.removeEventListener('keydown', this.deleteKeyHandler);
        this.container.removeEventListener('dragover', this.canvasDragOverHandler);
        this.container.removeEventListener('dragenter', this.canvasDragEnterHandler);
        this.container.removeEventListener('dragleave', this.canvasDragLeaveHandler);
        this.container.removeEventListener('drop', this.canvasDropHandler);
        document.removeEventListener('pointermove', this.resizeMoveHandler);
        document.removeEventListener('pointerup', this.resizeEndHandler);
        document.removeEventListener('pointercancel', this.resizeEndHandler);
        document.removeEventListener('pointermove', this.calloutTailMoveHandler);
        document.removeEventListener('pointerup', this.calloutTailEndHandler);
        document.removeEventListener('pointercancel', this.calloutTailEndHandler);
        this.cancelCalloutTailDrag();
        this.removeShiftSelectionHandlers();
        if (this.tempGroupSelectionElement) {
            this.tempGroupSelectionElement.remove();
            this.tempGroupSelectionElement = null;
        }
        this.shiftSelectionStart = null;
        document.body.style.cursor = '';
        this.resizeState = null;
    }
}

// called from C# to create a FlowEditor instance
export function createFlowEditor(options: FlowEditorOptions): FlowEditor {
    return new FlowEditor(options);
}
